import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Drawer, Form, Input, InputNumber, Modal, Row, Select, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { Eye, Plus, Search, Settings2, ShoppingCart, User, Wrench } from 'lucide-react';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import { useAuth } from '../contexts/AuthContext';
import {
  addConfiguredItemToCart,
  createConfigurableDefinition,
  getConfigurableDefinition,
  deleteConfigurableDefinition,
  getConfigurableDefinitionByProduct,
  listConfiguredCartItems,
  publishConfigurableDefinition,
  searchProductsSolr,
  updateConfiguredCartItem,
  updateConfigurableDefinition,
} from '../services/configurableProductApi';
import {
  getStepOptionChoices as getStepOptionChoicesUtil,
  getStepRestrictions as getStepRestrictionsUtil,
} from './configurableProductRulesUtils';

const blankStep = (idx = 0) => ({
  step_order: idx + 1,
  step_name: '',
  selection_mode: 'product',
  attribute_name: '',
  attribute_values: [],
  products: [],
  search_query: '',
  filters: { category: [], manufacturerName: [] },
  facets: {},
  search_results: [],
  search_loading: false,
  search_total: 0,
});

const blankDefinition = {
  id: null,
  product_code: '',
  name: '',
  description: '',
  is_active: false,
  workflow_state: 'draft',
  is_ready: false,
  has_published_version: false,
  published_product_code: '',
  include_parent_product_code: true,
  main_image_url: '',
  image_urls: [],
  conditional_rules: [],
  steps: [blankStep(0)],
};
const BUILDER_MAIN_IMAGE_CANVAS_HEIGHT = 320;
const BUILDER_THUMB_CANVAS_SIZE = 72;

const blankRuleCondition = () => ({
  id: `condition-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  source_step_order: 1,
  match_mode: 'any',
  option_refs: [],
});

const blankRuleGroup = () => ({
  id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  operator: 'AND',
  conditions: [blankRuleCondition()],
});

const blankConditionalRule = () => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  enabled: true,
  effect: 'hide',
  target_step_order: 1,
  target_option_refs: [],
  group_operator: 'OR',
  groups: [blankRuleGroup()],
});

const getRuleGuidanceSnapshot = (rule, getStepByOrder, getChoices) => {
  const targetStepOrder = Number(rule?.target_step_order || 0);
  const targetStep = targetStepOrder > 0 ? getStepByOrder(targetStepOrder) : null;
  const targetOptions = getChoices(targetStep);
  const targetOptionValues = new Set(targetOptions.map((item) => String(item?.value || '').trim()).filter(Boolean));
  const selectedTargets = Array.isArray(rule?.target_option_refs)
    ? rule.target_option_refs.map((ref) => String(ref || '').trim()).filter(Boolean)
    : [];
  const groups = Array.isArray(rule?.groups) ? rule.groups : [];
  const conditions = groups.flatMap((group) => (Array.isArray(group?.conditions) ? group.conditions : []));
  const hasConditionWithOptions = conditions.some((condition) => Array.isArray(condition?.option_refs) && condition.option_refs.length > 0);
  const hasConditionWithSource = conditions.some((condition) => Number(condition?.source_step_order || 0) > 0);
  const validTargetSelectionCount = selectedTargets.filter((ref) => targetOptionValues.has(ref)).length;
  const checklist = [
    { key: 'target_step', label: 'Choose target step', done: Boolean(targetStep) },
    { key: 'behavior', label: 'Choose behavior', done: ['hide', 'disable'].includes(String(rule?.effect || 'hide').trim().toLowerCase()) },
    { key: 'conditions', label: 'Add match conditions', done: groups.length > 0 && hasConditionWithSource && hasConditionWithOptions },
    { key: 'state', label: 'Enable rule', done: rule?.enabled !== false },
  ];
  const doneCount = checklist.filter((item) => item.done).length;
  const targetStepLabel = targetStep
    ? `${targetStep.step_order || targetStepOrder}: ${targetStep.step_name || 'Untitled'}`
    : 'not selected';
  const behaviorLabel = String(rule?.effect || 'hide').trim().toLowerCase() === 'disable' ? 'disable' : 'hide';
  const targetScopeLabel = selectedTargets.length
    ? `${validTargetSelectionCount || selectedTargets.length} selected options`
    : 'all options';
  return {
    checklist,
    doneCount,
    targetStepLabel,
    behaviorLabel,
    targetScopeLabel,
  };
};

const buildRulePlainLanguageExplanation = (rule, getStepByOrder, getChoices) => {
  const describeStep = (stepOrder) => {
    const step = getStepByOrder(stepOrder);
    if (!step) return `Step ${Number(stepOrder || 0) || '?'}`;
    return `Step ${step.step_order || stepOrder}: ${step.step_name || 'Untitled'}`;
  };

  const describeRefs = (stepLike, refs) => {
    const choices = getChoices(stepLike);
    const labelByValue = new Map(
      choices.map((item) => [String(item?.value || '').trim(), String(item?.label || item?.value || '').trim()]),
    );
    const normalized = Array.isArray(refs)
      ? refs.map((ref) => String(ref || '').trim()).filter(Boolean)
      : [];
    if (!normalized.length) return '';
    return normalized.map((ref) => labelByValue.get(ref) || ref).join(', ');
  };

  const effect = String(rule?.effect || 'hide').trim().toLowerCase() === 'disable' ? 'disable' : 'hide';
  const targetStep = getStepByOrder(rule?.target_step_order);
  const targetStepLabel = describeStep(rule?.target_step_order);
  const targetRefsText = describeRefs(targetStep, rule?.target_option_refs);
  const targetText = targetRefsText
    ? `${effect} ${targetRefsText} in ${targetStepLabel}`
    : `${effect} all options in ${targetStepLabel}`;

  const groups = Array.isArray(rule?.groups) ? rule.groups : [];
  if (!groups.length) {
    const statePrefix = rule?.enabled === false ? 'This rule is disabled. ' : '';
    return `${statePrefix}No trigger conditions are configured yet. It is set to ${targetText}.`;
  }

  const groupTexts = groups
    .map((group) => {
      const conditions = Array.isArray(group?.conditions) ? group.conditions : [];
      if (!conditions.length) return '';
      const conditionTexts = conditions
        .map((condition) => {
          const sourceStep = getStepByOrder(condition?.source_step_order);
          const sourceStepLabel = describeStep(condition?.source_step_order);
          const refText = describeRefs(sourceStep, condition?.option_refs);
          if (!refText) return `${sourceStepLabel} has no match options selected`;
          const matchMode = String(condition?.match_mode || 'any').trim().toLowerCase() === 'all' ? 'all of' : 'any of';
          return `${sourceStepLabel} is ${matchMode}: ${refText}`;
        })
        .filter(Boolean);
      if (!conditionTexts.length) return '';
      const groupJoiner = String(group?.operator || 'AND').trim().toUpperCase() === 'OR' ? ' OR ' : ' AND ';
      if (conditionTexts.length === 1) return conditionTexts[0];
      return `(${conditionTexts.join(groupJoiner)})`;
    })
    .filter(Boolean);

  const whenText = groupTexts.length
    ? groupTexts.join(String(rule?.group_operator || 'OR').trim().toUpperCase() === 'AND' ? ' AND ' : ' OR ')
    : 'trigger conditions are not fully configured';
  const statePrefix = rule?.enabled === false ? 'This rule is disabled. ' : '';
  return `${statePrefix}When ${whenText}, this rule will ${targetText}.`;
};

const normalizeDefinitionForSave = (draft) => ({
  product_code: String(draft.product_code || '').trim(),
  name: String(draft.name || '').trim(),
  description: String(draft.description || '').trim(),
  is_active: Boolean(draft.is_active),
  include_parent_product_code: Boolean(draft.include_parent_product_code !== false),
  main_image_url: String(draft.main_image_url || '').trim(),
  image_urls: (draft.image_urls || []).map((url) => String(url || '').trim()).filter(Boolean),
  conditional_rules: (draft.conditional_rules || []).map((rule, ruleIdx) => ({
    id: String(rule.id || `rule-${ruleIdx + 1}`),
    name: String(rule.name || `Rule ${ruleIdx + 1}`).trim() || `Rule ${ruleIdx + 1}`,
    enabled: Boolean(rule.enabled !== false),
    effect: String(rule.effect || 'hide').trim().toLowerCase() === 'disable' ? 'disable' : 'hide',
    target_step_order: Number(rule.target_step_order || 1),
    target_option_refs: Array.isArray(rule.target_option_refs)
      ? rule.target_option_refs.map((ref) => String(ref || '').trim()).filter(Boolean)
      : [],
    group_operator: String(rule.group_operator || 'OR').trim().toUpperCase() === 'AND' ? 'AND' : 'OR',
    groups: (rule.groups || []).map((group, groupIdx) => ({
      id: String(group.id || `group-${ruleIdx + 1}-${groupIdx + 1}`),
      operator: String(group.operator || 'AND').trim().toUpperCase() === 'OR' ? 'OR' : 'AND',
      conditions: (group.conditions || []).map((condition, condIdx) => ({
        id: String(condition.id || `condition-${ruleIdx + 1}-${groupIdx + 1}-${condIdx + 1}`),
        source_step_order: Number(condition.source_step_order || 1),
        match_mode: String(condition.match_mode || 'any').trim().toLowerCase() === 'all' ? 'all' : 'any',
        option_refs: Array.isArray(condition.option_refs)
          ? condition.option_refs.map((ref) => String(ref || '').trim()).filter(Boolean)
          : [],
      })),
    })),
  })),
  steps: (draft.steps || []).map((step, idx) => ({
    step_order: Number(step.step_order || idx + 1),
    step_name: String(step.step_name || '').trim(),
    selection_mode: String(step.selection_mode || 'product').trim().toLowerCase() === 'attribute' ? 'attribute' : 'product',
    attribute_name: String(step.attribute_name || '').trim(),
    products: (step.products || []).map((product, pIdx) => ({
      product_code: String(product.product_code || '').trim(),
      identifier: String(product.identifier || '').trim(),
      sort_order: Number(product.sort_order ?? pIdx),
    })),
    attribute_values: (step.attribute_values || []).map((item, aIdx) => ({
      value_label: String(item.value_label || '').trim(),
      identifier: String(item.identifier || '').trim(),
      sort_order: Number(item.sort_order ?? aIdx),
    })),
  })),
});

const ImageWithFallback = ({
  src,
  alt,
  imageStyle,
  fallbackText = 'No image',
  fallbackClassName = 'small text-muted',
}) => {
  const [failed, setFailed] = useState(false);
  const normalizedSrc = String(src || '').trim();

  useEffect(() => {
    setFailed(false);
  }, [normalizedSrc]);

  if (!normalizedSrc || failed) {
    return <span className={fallbackClassName}>{fallbackText}</span>;
  }

  return (
    <img
      src={normalizedSrc}
      alt={alt}
      style={imageStyle}
      onError={() => setFailed(true)}
    />
  );
};

const sanitizeRichHtml = (input) => {
  const source = String(input || '').trim();
  if (!source) return '';
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return source;
  }
  const container = document.createElement('div');
  container.innerHTML = source;

  container.querySelectorAll('script, style, iframe, object, embed').forEach((node) => node.remove());

  const allNodes = container.querySelectorAll('*');
  allNodes.forEach((node) => {
    const attrs = Array.from(node.attributes || []);
    attrs.forEach((attr) => {
      const attrName = String(attr.name || '').toLowerCase();
      const attrValue = String(attr.value || '').trim().toLowerCase();
      if (attrName.startsWith('on')) {
        node.removeAttribute(attr.name);
        return;
      }
      if ((attrName === 'href' || attrName === 'src') && /^javascript:/i.test(attrValue)) {
        node.removeAttribute(attr.name);
      }
    });
  });

  return container.innerHTML;
};

const RichHtmlBlock = ({ html, fallback = '', className = '' }) => {
  const safeHtml = useMemo(() => sanitizeRichHtml(html), [html]);
  if (!safeHtml) {
    return <Typography.Text type="secondary" className={className}>{fallback}</Typography.Text>;
  }
  return <div className={className} dangerouslySetInnerHTML={{ __html: safeHtml }} />;
};

const HtmlRichTextEditor = ({
  value,
  onChange,
  placeholder = '',
  modules,
  formats,
  minHeight = 260,
}) => {
  const skipNextChangeRef = useRef(false);
  const { quill, quillRef } = useQuill({
    theme: 'snow',
    modules,
    formats,
    placeholder,
  });

  useEffect(() => {
    if (!quill) return;
    const normalized = String(value || '').trim();
    const current = String(quill.root?.innerHTML || '').trim();
    if (current === normalized) return;
    skipNextChangeRef.current = true;
    if (!normalized) {
      quill.setText('');
    } else {
      quill.clipboard.dangerouslyPasteHTML(normalized);
    }
  }, [quill, value]);

  useEffect(() => {
    if (!quill) return undefined;
    const onTextChange = () => {
      if (skipNextChangeRef.current) {
        skipNextChangeRef.current = false;
        return;
      }
      const html = String(quill.root?.innerHTML || '');
      const normalized = html === '<p><br></p>' ? '' : html;
      if (typeof onChange === 'function') onChange(normalized);
    };
    quill.on('text-change', onTextChange);
    return () => {
      quill.off('text-change', onTextChange);
    };
  }, [quill, onChange]);

  return (
    <div className="border rounded bg-white">
      <div ref={quillRef} style={{ minHeight }} />
    </div>
  );
};

const ConfigurableProducts = () => {
  const { getAuthHeaders, account } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { productCode: routeProductCode, definitionId } = useParams();
  const [searchParams] = useSearchParams();
  const initialProductCode = String(searchParams.get('product') || '').trim();
  const mode = routeProductCode ? 'builder' : 'editor';
  const builderView = mode === 'builder' && location.pathname.endsWith('/cart') ? 'cart' : 'configurator';

  const [editor, setEditor] = useState({ ...blankDefinition });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [builderDefinition, setBuilderDefinition] = useState(null);
  const [builderSelections, setBuilderSelections] = useState({});
  const [builderQuantity, setBuilderQuantity] = useState(1);
  const [builderUserId, setBuilderUserId] = useState('');
  const [builderUserDraft, setBuilderUserDraft] = useState('');
  const [builderUserEditMode, setBuilderUserEditMode] = useState(true);
  const [builderCart, setBuilderCart] = useState([]);
  const [builderCartDetailOpen, setBuilderCartDetailOpen] = useState(false);
  const [builderCartSelectedItem, setBuilderCartSelectedItem] = useState(null);
  const [builderCartEditMode, setBuilderCartEditMode] = useState(false);
  const [builderCartEditSelections, setBuilderCartEditSelections] = useState({});
  const [builderCartEditQuantity, setBuilderCartEditQuantity] = useState(1);
  const [builderCartSavingEdit, setBuilderCartSavingEdit] = useState(false);
  const [detailModal, setDetailModal] = useState({ open: false, product: null });
  const [builderActiveImage, setBuilderActiveImage] = useState('');
  const [builderZoomImage, setBuilderZoomImage] = useState('');
  const [builderZoomOpen, setBuilderZoomOpen] = useState(false);
  const [builderZoomScale, setBuilderZoomScale] = useState(1);
  const [currentBuilderStepIdx, setCurrentBuilderStepIdx] = useState(0);
  const [activeEditorStepKey, setActiveEditorStepKey] = useState('0');
  const [editorPreviewImage, setEditorPreviewImage] = useState('');
  const [editorZoomImage, setEditorZoomImage] = useState('');
  const [editorZoomOpen, setEditorZoomOpen] = useState(false);
  const [editorZoomScale, setEditorZoomScale] = useState(1);
  const [activeEditorWorkspaceTab, setActiveEditorWorkspaceTab] = useState('productInfo');
  const [appAlert, setAppAlert] = useState({ open: false, title: 'Notice', message: '' });
  const [appConfirm, setAppConfirm] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [rulesHelpOpen, setRulesHelpOpen] = useState(false);
  const [ruleEditorDrawerOpen, setRuleEditorDrawerOpen] = useState(false);
  const [activeRuleEditorIdx, setActiveRuleEditorIdx] = useState(null);
  const [ruleDrawerWidthPct, setRuleDrawerWidthPct] = useState(50);
  const builderStepCardRef = useRef(null);
  const descriptionEditorModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      [{ align: [] }, { direction: 'rtl' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
  }), []);
  const descriptionEditorFormats = useMemo(
    () => [
      'header',
      'font',
      'size',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'script',
      'list',
      'bullet',
      'indent',
      'align',
      'direction',
      'blockquote',
      'code-block',
      'link',
      'image',
      'video',
    ],
    [],
  );
  const showAlert = useCallback((message, title = 'Notice') => {
    setAppAlert({ open: true, title, message: String(message || '') });
  }, []);

  const showConfirm = useCallback((title, message, onConfirm) => {
    setAppConfirm({
      open: true,
      title: String(title || 'Confirm action'),
      message: String(message || ''),
      onConfirm: typeof onConfirm === 'function' ? onConfirm : null,
    });
  }, []);
  const updateRuleAtIndex = useCallback((ruleIdx, updater) => {
    setEditor((prev) => {
      const nextRules = [...(prev.conditional_rules || [])];
      if (ruleIdx < 0 || ruleIdx >= nextRules.length) return prev;
      const current = { ...(nextRules[ruleIdx] || {}) };
      const updated = typeof updater === 'function' ? updater(current) : current;
      nextRules[ruleIdx] = updated;
      return { ...prev, conditional_rules: nextRules };
    });
  }, []);
  const handleRuleDrawerResizeStart = useCallback((event) => {
    event.preventDefault();
    const onMouseMove = (moveEvent) => {
      if (typeof window === 'undefined') return;
      const width = Math.max(1024, Number(window.innerWidth || 0));
      const pct = ((width - Number(moveEvent.clientX || 0)) / width) * 100;
      const clamped = Math.min(75, Math.max(40, pct));
      setRuleDrawerWidthPct(Number(clamped.toFixed(1)));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);
  useEffect(() => {
    if (mode !== 'editor' || !initialProductCode || definitionId) return;
    setEditor((prev) => {
      if (prev.id) return prev;
      return { ...prev, product_code: prev.product_code || initialProductCode, name: prev.name || initialProductCode };
    });
  }, [mode, initialProductCode, definitionId]);

  const loadDefinitionDetails = useCallback(async (definitionId) => {
    if (!definitionId) return;
    try {
      const data = await getConfigurableDefinition(definitionId, getAuthHeaders());
      setEditor({
        id: data.id,
        product_code: data.product_code || '',
        name: data.name || '',
        description: data.description || '',
        is_active: data.is_active !== false,
        workflow_state: data.workflow_state || 'draft',
        is_ready: Boolean(data.is_ready),
        has_published_version: Boolean(data.has_published_version),
        published_product_code: data.published_product_code || '',
        include_parent_product_code: data.include_parent_product_code !== false,
        main_image_url: data.main_image_url || '',
        image_urls: Array.isArray(data.image_urls) ? data.image_urls : [],
        conditional_rules: Array.isArray(data.conditional_rules)
          ? data.conditional_rules
          : [],
        steps: (data.steps || []).map((step, idx) => ({
          ...blankStep(idx),
          step_order: Number(step.step_order || idx + 1),
          step_name: step.step_name || '',
          selection_mode: step.selection_mode === 'attribute' ? 'attribute' : 'product',
          attribute_name: step.attribute_name || '',
          attribute_values: (step.attribute_values || []).map((item, aIdx) => ({
            value_label: item.value_label || '',
            identifier: item.identifier || '',
            sort_order: Number(item.sort_order ?? aIdx),
          })),
          products: (step.products || []).map((item, pIdx) => ({
            product_code: item.product_code,
            identifier: item.identifier || '',
            sort_order: Number(item.sort_order ?? pIdx),
            product: item.product || {},
          })),
        })),
      });
    } catch (err) {
      // non-blocking for list mode
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (mode === 'editor' && definitionId) {
      setLoading(true);
      loadDefinitionDetails(Number(definitionId))
        .finally(() => setLoading(false));
    }
  }, [mode, definitionId, loadDefinitionDetails]);

  const runStepSearch = async (stepIndex) => {
    const step = editor.steps?.[stepIndex];
    if (!step) return;
    setEditor((prev) => {
      const steps = [...(prev.steps || [])];
      steps[stepIndex] = { ...(steps[stepIndex] || blankStep(stepIndex)), search_loading: true };
      return { ...prev, steps };
    });
    try {
      const payload = {
        query: String(step.search_query || '').trim(),
        page: 1,
        page_size: 25,
        filters: step.filters || {},
      };
      const data = await searchProductsSolr(payload, getAuthHeaders());
      setEditor((prev) => {
        const steps = [...(prev.steps || [])];
        const target = { ...(steps[stepIndex] || blankStep(stepIndex)) };
        target.search_results = data?.docs || [];
        target.facets = data?.facets || {};
        target.search_total = Number(data?.numFound || 0);
        steps[stepIndex] = target;
        return { ...prev, steps };
      });
    } catch (err) {
      showAlert(err.message || 'Product search failed', 'Search Error');
    } finally {
      setEditor((prev) => {
        const steps = [...(prev.steps || [])];
        const target = { ...(steps[stepIndex] || blankStep(stepIndex)) };
        target.search_loading = false;
        steps[stepIndex] = target;
        return { ...prev, steps };
      });
    }
  };

  const addProductsToStep = (stepIndex, selectedRows) => {
    setEditor((prev) => {
      const steps = [...(prev.steps || [])];
      const target = { ...(steps[stepIndex] || blankStep(stepIndex)) };
      const existingByCode = new Map((target.products || []).map((item) => [item.product_code, item]));
      selectedRows.forEach((row) => {
        const code = String(row.productCode || row.product_code || '').trim();
        if (!code) return;
        if (!existingByCode.has(code)) {
          existingByCode.set(code, {
            product_code: code,
            identifier: '',
            sort_order: existingByCode.size,
            product: {
              name: row.name,
              manufacturer_part_number: row.manufacturerPartNumber,
              image_url: row.imageUrl,
            },
          });
        }
      });
      target.products = Array.from(existingByCode.values());
      steps[stepIndex] = target;
      return { ...prev, steps };
    });
  };

  const addAttributeValueToStep = (stepIndex) => {
    setEditor((prev) => {
      const steps = [...(prev.steps || [])];
      const target = { ...(steps[stepIndex] || blankStep(stepIndex)) };
      const nextValues = [...(target.attribute_values || [])];
      nextValues.push({ value_label: '', identifier: '', sort_order: nextValues.length });
      target.attribute_values = nextValues;
      steps[stepIndex] = target;
      return { ...prev, steps };
    });
  };

  const pasteAttributeValuesFromClipboard = useCallback(async (stepIndex) => {
    try {
      const raw = await navigator.clipboard.readText();
      const lines = String(raw || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) {
        showAlert('Clipboard is empty.', 'Clipboard');
        return;
      }
      const parsed = [];
      lines.forEach((line) => {
        let parts = line.split('\t');
        if (parts.length < 2) parts = line.split('|');
        if (parts.length < 2) parts = line.split(',');
        if (parts.length < 2) return;
        const valueLabel = String(parts[0] || '').trim();
        const identifier = String(parts.slice(1).join(' ') || '').trim();
        if (!valueLabel || !identifier) return;
        parsed.push({ value_label: valueLabel, identifier });
      });
      if (!parsed.length) {
        showAlert('No valid rows found. Expected format per line: value<TAB>identifier', 'Clipboard');
        return;
      }
      setEditor((prev) => {
        const steps = [...(prev.steps || [])];
        const target = { ...(steps[stepIndex] || blankStep(stepIndex)) };
        const existing = [...(target.attribute_values || [])];
        const existingKey = new Set(existing.map((item) => `${item.value_label}::${item.identifier}`.toLowerCase()));
        parsed.forEach((item) => {
          const key = `${item.value_label}::${item.identifier}`.toLowerCase();
          if (existingKey.has(key)) return;
          existingKey.add(key);
          existing.push({
            value_label: item.value_label,
            identifier: item.identifier,
            sort_order: existing.length,
          });
        });
        target.attribute_values = existing;
        steps[stepIndex] = target;
        return { ...prev, steps };
      });
    } catch (err) {
      showAlert('Unable to read clipboard. Please allow clipboard access and try again.', 'Clipboard');
    }
  }, [showAlert]);

  const saveDefinition = async () => {
    const payload = normalizeDefinitionForSave(editor);
    if (!payload.product_code || !payload.name) {
      showAlert('Base product code and name are required.', 'Validation');
      return;
    }
    setSaving(true);
    try {
      let targetId = editor.id ? Number(editor.id) : null;
      if (editor.id) {
        await updateConfigurableDefinition(editor.id, payload, getAuthHeaders());
      } else {
        const created = await createConfigurableDefinition(payload, getAuthHeaders());
        if (created?.id) {
          targetId = Number(created.id);
          navigate(`/products/configurable/edit/${created.id}`);
        }
      }
      if (targetId) {
        await loadDefinitionDetails(targetId);
      }
      showAlert('Draft saved. You can continue configuring before publishing.', 'Draft Saved');
    } catch (err) {
      showAlert(err.message || 'Unable to save configurable product', 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const publishDefinition = async () => {
    if (!editor.id) {
      showAlert('Save a draft first, then publish.', 'Publish');
      return;
    }
    setSaving(true);
    try {
      const published = await publishConfigurableDefinition(editor.id, getAuthHeaders());
      await loadDefinitionDetails(Number(published?.id || editor.id));
      showAlert('Configurable product published. Draft is now cleared.', 'Published');
    } catch (err) {
      showAlert(err.message || 'Unable to publish configurable product', 'Publish Error');
    } finally {
      setSaving(false);
    }
  };

  const removeDefinition = async () => {
    if (!editor.id) return;
    showConfirm(
      'Delete Configurable Product',
      'Delete this configurable product definition?',
      async () => {
        try {
          await deleteConfigurableDefinition(editor.id, getAuthHeaders());
          navigate('/products/configurable');
        } catch (err) {
          showAlert(err.message || 'Unable to delete', 'Delete Error');
        }
      },
    );
  };

  const loadBuilder = useCallback(async () => {
    if (!routeProductCode) return;
    try {
      const data = await getConfigurableDefinitionByProduct(routeProductCode, getAuthHeaders());
      setBuilderDefinition(data);
      setBuilderSelections({});
    } catch (err) {
      setBuilderDefinition(null);
      showAlert(err.message || 'Configurable setup not found for this product', 'Load Error');
    }
  }, [routeProductCode, getAuthHeaders, showAlert]);

  useEffect(() => {
    if (mode === 'builder' && routeProductCode) {
      loadBuilder();
    }
  }, [mode, routeProductCode, loadBuilder]);

  const builderGalleryImages = useMemo(() => {
    const main = String(builderDefinition?.main_image_url || '').trim();
    const extras = Array.isArray(builderDefinition?.image_urls) ? builderDefinition.image_urls : [];
    const merged = [main, ...extras].map((item) => String(item || '').trim()).filter(Boolean);
    return Array.from(new Set(merged));
  }, [builderDefinition?.main_image_url, builderDefinition?.image_urls]);

  useEffect(() => {
    if (mode !== 'builder') return;
    const images = builderGalleryImages;
    if (!images.length) {
      if (builderActiveImage) setBuilderActiveImage('');
      return;
    }
    const active = String(builderActiveImage || '').trim();
    if (active && images.includes(active)) {
      return;
    }
    const main = String(builderDefinition?.main_image_url || '').trim();
    if (main && images.includes(main)) {
      setBuilderActiveImage(main);
      return;
    }
    setBuilderActiveImage(images[0]);
  }, [mode, builderDefinition?.main_image_url, builderGalleryImages, builderActiveImage]);

  const builtPartSegments = useMemo(() => {
    if (!builderDefinition) return [];
    const base = String(builderDefinition.product_code || '').trim();
    const pieces = builderDefinition.include_parent_product_code !== false && base ? [base] : [];
    (builderDefinition.steps || []).forEach((step) => {
      const selectedVal = builderSelections[step.id];
      const isAttribute = String(step.selection_mode || 'product') === 'attribute';
      const option = isAttribute
        ? (step.attribute_values || []).find((item) => item.value_label === selectedVal)
        : (step.products || []).find((item) => item.product_code === selectedVal);
      if (option?.identifier) pieces.push(option.identifier);
    });
    return pieces.filter(Boolean);
  }, [builderDefinition, builderSelections]);
  const builtPartNumber = useMemo(() => builtPartSegments.join('-'), [builtPartSegments]);

  const canAddToCart = useMemo(() => {
    if (!builderDefinition) return false;
    const requiredSteps = builderDefinition.steps || [];
    if (!requiredSteps.length) return false;
    const selectedCount = requiredSteps.filter((step) => Boolean(builderSelections[step.id])).length;
    return selectedCount === requiredSteps.length && String(builderUserId || '').trim() !== '';
  }, [builderDefinition, builderSelections, builderUserId]);

  const addToCart = async () => {
    if (!builderDefinition) return;
    const selectedByOrder = {};
    (builderDefinition.steps || []).forEach((step, idx) => {
      selectedByOrder[Number(step.step_order || idx + 1)] = builderSelections?.[step.id] || '';
    });
    const restrictedSteps = (builderDefinition.steps || []).filter((step, idx) => {
      const selected = String(builderSelections?.[step.id] || '').trim();
      if (!selected) return false;
      const restrictions = getStepRestrictions(step, selectedByOrder, activeConditionalRules, getBuilderStepByOrder);
      return restrictions.hidden.has(selected) || restrictions.disabled.has(selected);
    });
    if (restrictedSteps.length > 0) {
      const names = restrictedSteps.map((step, idx) => step.step_name || `Step ${idx + 1}`).join(', ');
      showAlert(`Please update restricted selections before adding to cart: ${names}`, 'Rule Validation');
      return;
    }
    const selections = (builderDefinition.steps || []).map((step) => {
      const isAttribute = String(step.selection_mode || 'product') === 'attribute';
      return {
        step_id: step.id,
        ...(isAttribute
          ? { attribute_value: builderSelections[step.id] }
          : { product_code: builderSelections[step.id] }),
      };
    });
    try {
      await addConfiguredItemToCart(
        builderDefinition.id,
        {
          user_id: builderUserId.trim(),
          quantity: builderQuantity,
          selections,
        },
        getAuthHeaders(),
      );
      const cart = await listConfiguredCartItems(builderUserId.trim(), getAuthHeaders());
      setBuilderCart(cart?.items || []);
      showAlert('Configured item added to cart.', 'Success');
    } catch (err) {
      showAlert(err.message || 'Unable to add configured item to cart', 'Cart Error');
    }
  };

  const loadBuilderCart = useCallback(async (userIdValue) => {
    const val = String(userIdValue || '').trim();
    if (!val) {
      setBuilderCart([]);
      return;
    }
    try {
      const cart = await listConfiguredCartItems(val, getAuthHeaders());
      setBuilderCart(cart?.items || []);
    } catch (err) {
      // keep UI usable even if cart fetch fails
      setBuilderCart([]);
    }
  }, [getAuthHeaders]);

  const openProductDetails = (productLike, fallbackCode = '') => {
    if (!productLike && !fallbackCode) return;
    const normalized = {
      productCode: productLike?.productCode || productLike?.product_code || fallbackCode || '—',
      name: productLike?.name || '—',
      category: productLike?.category || '—',
      manufacturerName: productLike?.manufacturerName || productLike?.manufacturer_name || '—',
      manufacturerPartNumber: productLike?.manufacturerPartNumber || productLike?.manufacturer_part_number || '—',
      imageUrl: productLike?.imageUrl || productLike?.image_url || '',
      description: productLike?.description || '',
    };
    setDetailModal({ open: true, product: normalized });
  };

  const builderUserStorageKey = useMemo(() => {
    const tenantId = account?.id != null ? String(account.id) : 'global';
    return `configurable_builder_user_id:${tenantId}`;
  }, [account?.id]);

  useEffect(() => {
    if (mode !== 'builder') return;
    try {
      const cached = localStorage.getItem(builderUserStorageKey) || '';
      const normalized = String(cached || '').trim();
      if (normalized) {
        setBuilderUserId(normalized);
        setBuilderUserDraft(normalized);
        setBuilderUserEditMode(false);
      } else {
        setBuilderUserId('');
        setBuilderUserDraft('');
        setBuilderUserEditMode(true);
      }
    } catch (err) {
      setBuilderUserId('');
      setBuilderUserDraft('');
      setBuilderUserEditMode(true);
    }
  }, [mode, builderUserStorageKey]);

  useEffect(() => {
    if (mode !== 'builder') return;
    if (!builderUserId) return;
    loadBuilderCart(builderUserId);
  }, [mode, builderUserId, loadBuilderCart]);

  const isBuilderStepAutoSkippable = useCallback((step) => {
    if (!step) return false;
    const isAttribute = String(step.selection_mode || 'product') === 'attribute';
    const options = isAttribute ? (step.attribute_values || []) : (step.products || []);
    return options.length === 1;
  }, []);

  const autoSelectSingleOption = useCallback((step) => {
    if (!step) return null;
    const isAttribute = String(step.selection_mode || 'product') === 'attribute';
    const options = isAttribute ? (step.attribute_values || []) : (step.products || []);
    if (options.length !== 1) return null;
    return isAttribute ? options[0]?.value_label : options[0]?.product_code;
  }, []);

  const getNextNonAutoStepIndex = useCallback((startIdx, direction = 1) => {
    const steps = builderDefinition?.steps || [];
    if (!steps.length) return 0;
    let idx = startIdx;
    while (idx >= 0 && idx < steps.length) {
      if (!isBuilderStepAutoSkippable(steps[idx])) return idx;
      idx += direction;
    }
    return direction > 0 ? steps.length - 1 : 0;
  }, [builderDefinition, isBuilderStepAutoSkippable]);

  const builderStepSummary = useMemo(() => {
    const steps = builderDefinition?.steps || [];
    return steps.map((step) => {
      const isAttribute = String(step.selection_mode || 'product') === 'attribute';
      const selectedValue = builderSelections[step.id] || autoSelectSingleOption(step);
      const autoSelected = isBuilderStepAutoSkippable(step);
      if (!selectedValue) {
        return {
          stepId: step.id,
          stepName: step.step_name,
          completed: false,
          label: 'Not selected',
          identifier: '',
          autoSelected: false,
        };
      }
      if (isAttribute) {
        const picked = (step.attribute_values || []).find((item) => item.value_label === selectedValue);
        return {
          stepId: step.id,
          stepName: step.step_name,
          completed: Boolean(picked),
          label: picked?.value_label || selectedValue,
          identifier: picked?.identifier || '',
          autoSelected,
        };
      }
      const picked = (step.products || []).find((item) => item.product_code === selectedValue);
      return {
        stepId: step.id,
        stepName: step.step_name,
        completed: Boolean(picked),
        label: picked?.product?.name || picked?.product_code || selectedValue,
        identifier: picked?.identifier || '',
        autoSelected,
      };
    });
  }, [builderDefinition, builderSelections, autoSelectSingleOption, isBuilderStepAutoSkippable]);

  const completedBuilderSteps = useMemo(
    () => builderStepSummary.filter((item) => item.completed).length,
    [builderStepSummary],
  );

  const selectedCartItemSelectionRows = useMemo(() => {
    const raw = builderCartSelectedItem?.selections_json;
    const selections = Array.isArray(raw) ? raw : [];
    const orderByStepId = new Map((builderDefinition?.steps || []).map((step, idx) => [step.id, idx]));
    return [...selections].sort((a, b) => {
      const aIdx = orderByStepId.get(a?.step_id) ?? Number.MAX_SAFE_INTEGER;
      const bIdx = orderByStepId.get(b?.step_id) ?? Number.MAX_SAFE_INTEGER;
      return aIdx - bIdx;
    });
  }, [builderCartSelectedItem, builderDefinition]);

  const editedCartPartSegments = useMemo(() => {
    if (!builderDefinition || !builderCartEditMode) return [];
    const base = String(builderDefinition.product_code || '').trim();
    const pieces = builderDefinition.include_parent_product_code !== false && base ? [base] : [];
    (builderDefinition.steps || []).forEach((step) => {
      const selectedVal = builderCartEditSelections[step.id];
      const isAttribute = String(step.selection_mode || 'product') === 'attribute';
      const option = isAttribute
        ? (step.attribute_values || []).find((item) => item.value_label === selectedVal)
        : (step.products || []).find((item) => item.product_code === selectedVal);
      if (option?.identifier) pieces.push(option.identifier);
    });
    return pieces.filter(Boolean);
  }, [builderDefinition, builderCartEditMode, builderCartEditSelections]);
  const editedCartPartNumber = useMemo(() => editedCartPartSegments.join('-'), [editedCartPartSegments]);
  const canSaveCartEdits = useMemo(() => {
    if (!builderCartEditMode || !builderDefinition) return false;
    const requiredSteps = builderDefinition.steps || [];
    if (!requiredSteps.length) return false;
    const selectedCount = requiredSteps.filter((step) => Boolean(builderCartEditSelections[step.id])).length;
    return selectedCount === requiredSteps.length && String(builderUserId || '').trim() !== '';
  }, [builderCartEditMode, builderDefinition, builderCartEditSelections, builderUserId]);

  const openBuilderCartItem = useCallback((row, startEdit = false) => {
    setBuilderCartSelectedItem(row);
    const selectionRows = Array.isArray(row?.selections_json) ? row.selections_json : [];
    const mapped = {};
    selectionRows.forEach((item) => {
      const stepId = Number(item?.step_id);
      if (!Number.isFinite(stepId)) return;
      mapped[stepId] = item?.attribute_value || item?.product_code || '';
    });
    if (startEdit) {
      setBuilderCartEditSelections(mapped);
      setBuilderCartEditQuantity(Number(row?.quantity || 1));
    }
    setBuilderCartEditMode(Boolean(startEdit));
    setBuilderCartDetailOpen(true);
  }, []);

  const handleSaveBuilderCartEdits = useCallback(async () => {
    if (!builderCartSelectedItem || !builderDefinition) return;
    const selections = (builderDefinition.steps || []).map((step) => {
      const isAttribute = String(step.selection_mode || 'product') === 'attribute';
      return {
        step_id: step.id,
        ...(isAttribute
          ? { attribute_value: builderCartEditSelections[step.id] }
          : { product_code: builderCartEditSelections[step.id] }),
      };
    });
    setBuilderCartSavingEdit(true);
    try {
      await updateConfiguredCartItem(
        builderCartSelectedItem.id,
        {
          user_id: builderUserId.trim(),
          quantity: builderCartEditQuantity,
          selections,
        },
        getAuthHeaders(),
      );
      const cart = await listConfiguredCartItems(builderUserId.trim(), getAuthHeaders());
      const items = cart?.items || [];
      setBuilderCart(items);
      const refreshed = items.find((item) => item.id === builderCartSelectedItem.id);
      if (refreshed) setBuilderCartSelectedItem(refreshed);
      setBuilderCartEditMode(false);
      showAlert('Configured cart item updated.', 'Success');
    } catch (err) {
      showAlert(err.message || 'Unable to update configured cart item', 'Update Error');
    } finally {
      setBuilderCartSavingEdit(false);
    }
  }, [
    builderCartSelectedItem,
    builderDefinition,
    builderCartEditSelections,
    builderCartEditQuantity,
    builderUserId,
    getAuthHeaders,
    showAlert,
  ]);

  const handleBuilderStepSelection = useCallback((step, value) => {
    if (!step) return;
    const steps = builderDefinition?.steps || [];
    const resolveBuilderStepByOrder = (stepOrder) => steps.find((item, idx) => Number(item.step_order || idx + 1) === Number(stepOrder));
    const localActiveRules = (builderDefinition?.conditional_rules || []).filter((rule) => rule?.enabled !== false);
    const selectedByOrder = {};
    steps.forEach((item, idx) => {
      const order = Number(item.step_order || idx + 1);
      selectedByOrder[order] = builderSelections?.[item.id] || '';
    });
    const stepOrder = Number(step.step_order || 0);
    const nextSelectedByOrder = { ...selectedByOrder, [stepOrder]: value };
    const restrictions = getStepRestrictionsUtil(
      step,
      nextSelectedByOrder,
      localActiveRules,
      getStepOptionChoicesUtil,
      resolveBuilderStepByOrder,
    );
    if (restrictions.hidden.has(String(value || '').trim()) || restrictions.disabled.has(String(value || '').trim())) {
      const reasons = [
        ...(restrictions.hiddenReasons.get(String(value || '').trim()) || []),
        ...(restrictions.disabledReasons.get(String(value || '').trim()) || []),
      ];
      const reasonText = reasons.join('; ') || 'Restricted by active rules';
      showAlert(reasonText, 'Rule Applied');
      return;
    }
    setBuilderSelections((prev) => ({ ...prev, [step.id]: value }));
    const currentIdx = steps.findIndex((item) => item.id === step.id);
    if (currentIdx < 0) return;
    if (currentIdx >= steps.length - 1) return;
    const nextIdx = getNextNonAutoStepIndex(currentIdx + 1, 1);
    if (nextIdx !== currentIdx) {
      setCurrentBuilderStepIdx(nextIdx);
      setTimeout(() => {
        builderStepCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  }, [
    builderDefinition,
    builderSelections,
    getNextNonAutoStepIndex,
    showAlert,
  ]);

  useEffect(() => {
    if (mode !== 'builder') return;
    const steps = builderDefinition?.steps || [];
    if (!steps.length) {
      setCurrentBuilderStepIdx(0);
      return;
    }
    setBuilderSelections((prev) => {
      const next = { ...prev };
      let changed = false;
      steps.forEach((step) => {
        const autoVal = autoSelectSingleOption(step);
        if (!autoVal) return;
        if (!next[step.id]) {
          next[step.id] = autoVal;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setCurrentBuilderStepIdx((prevIdx) => {
      const safe = Math.min(Math.max(prevIdx, 0), steps.length - 1);
      return getNextNonAutoStepIndex(safe, 1);
    });
  }, [mode, builderDefinition, autoSelectSingleOption, getNextNonAutoStepIndex]);

  useEffect(() => {
    if (mode !== 'editor') return;
    const steps = editor.steps || [];
    if (!steps.length) {
      setActiveEditorStepKey('0');
      return;
    }
    const idx = Number(activeEditorStepKey);
    if (!Number.isFinite(idx) || idx < 0 || idx >= steps.length) {
      setActiveEditorStepKey(String(Math.max(0, steps.length - 1)));
    }
  }, [mode, editor.steps, activeEditorStepKey]);

  useEffect(() => {
    if (mode !== 'editor') return;
    const main = String(editor.main_image_url || '').trim();
    const gallery = [main, ...(editor.image_urls || [])]
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    const normalizedGallery = Array.from(new Set(gallery));
    if (!normalizedGallery.length) {
      if (editorPreviewImage) setEditorPreviewImage('');
      return;
    }
    const currentInGallery = normalizedGallery.includes(String(editorPreviewImage || '').trim());
    if (currentInGallery) return;
    if (main) {
      setEditorPreviewImage(main);
      return;
    }
    setEditorPreviewImage(normalizedGallery[0]);
  }, [mode, editor.main_image_url, editor.image_urls, editorPreviewImage]);

  const editorGalleryImages = useMemo(() => {
    const main = String(editor.main_image_url || '').trim();
    const extras = Array.isArray(editor.image_urls) ? editor.image_urls : [];
    const merged = [main, ...extras].map((item) => String(item || '').trim()).filter(Boolean);
    return Array.from(new Set(merged));
  }, [editor.main_image_url, editor.image_urls]);

  const editorStepOrderOptions = useMemo(
    () => (editor.steps || []).map((step, idx) => ({
      value: Number(step.step_order || idx + 1),
      label: `Step ${step.step_order || idx + 1}: ${step.step_name || 'Untitled'}`,
      mode: step.selection_mode === 'attribute' ? 'attribute' : 'product',
    })),
    [editor.steps],
  );

  const getEditorStepByOrder = useCallback(
    (stepOrder) => (editor.steps || []).find((step, idx) => Number(step.step_order || idx + 1) === Number(stepOrder)),
    [editor.steps],
  );
  const activeRuleEditor = useMemo(() => {
    if (activeRuleEditorIdx == null) return null;
    return (editor.conditional_rules || [])[activeRuleEditorIdx] || null;
  }, [editor.conditional_rules, activeRuleEditorIdx]);
  const getBuilderStepByOrder = useCallback(
    (stepOrder) => (builderDefinition?.steps || []).find((step, idx) => Number(step.step_order || idx + 1) === Number(stepOrder)),
    [builderDefinition],
  );

  const getStepOptionChoices = useCallback((stepLike) => {
    return getStepOptionChoicesUtil(stepLike);
  }, []);

  const getStepRestrictions = useCallback((stepLike, selectedValueByStepOrder, rulesRaw, resolveStepByOrder) => {
    return getStepRestrictionsUtil(stepLike, selectedValueByStepOrder, rulesRaw, getStepOptionChoices, resolveStepByOrder);
  }, [getStepOptionChoices]);

  const builderSelectedValueByStepOrder = useMemo(() => {
    const map = {};
    (builderDefinition?.steps || []).forEach((step, idx) => {
      const stepOrder = Number(step.step_order || idx + 1);
      map[stepOrder] = builderSelections?.[step.id] || autoSelectSingleOption(step) || '';
    });
    return map;
  }, [builderDefinition, builderSelections, autoSelectSingleOption]);

  const activeConditionalRules = useMemo(
    () => (builderDefinition?.conditional_rules || []).filter((rule) => rule?.enabled !== false),
    [builderDefinition?.conditional_rules],
  );

  const builderRestrictedStepNames = useMemo(() => {
    if (!builderDefinition) return [];
    const names = [];
    (builderDefinition.steps || []).forEach((step, idx) => {
      const selected = String(builderSelections?.[step.id] || '').trim();
      if (!selected) return;
      const restrictions = getStepRestrictions(step, builderSelectedValueByStepOrder, activeConditionalRules, getBuilderStepByOrder);
      if (restrictions.hidden.has(selected) || restrictions.disabled.has(selected)) {
        names.push(step.step_name || `Step ${idx + 1}`);
      }
    });
    return names;
  }, [
    builderDefinition,
    builderSelections,
    builderSelectedValueByStepOrder,
    activeConditionalRules,
    getStepRestrictions,
    getBuilderStepByOrder,
  ]);

  const cartEditRestrictedStepNames = useMemo(() => {
    if (!builderCartEditMode || !builderDefinition) return [];
    const selectedByOrder = {};
    (builderDefinition.steps || []).forEach((step, idx) => {
      selectedByOrder[Number(step.step_order || idx + 1)] = builderCartEditSelections?.[step.id] || '';
    });
    const names = [];
    (builderDefinition.steps || []).forEach((step, idx) => {
      const selected = String(builderCartEditSelections?.[step.id] || '').trim();
      if (!selected) return;
      const restrictions = getStepRestrictions(step, selectedByOrder, activeConditionalRules, getBuilderStepByOrder);
      if (restrictions.hidden.has(selected) || restrictions.disabled.has(selected)) {
        names.push(step.step_name || `Step ${idx + 1}`);
      }
    });
    return names;
  }, [
    builderCartEditMode,
    builderDefinition,
    builderCartEditSelections,
    activeConditionalRules,
    getStepRestrictions,
    getBuilderStepByOrder,
  ]);

  useEffect(() => {
    if (mode !== 'builder' || !builderDefinition) return;
    const steps = builderDefinition.steps || [];
    const hiddenToClear = [];
    const disabledToClear = [];
    steps.forEach((step, idx) => {
      const selected = String(builderSelections?.[step.id] || '').trim();
      if (!selected) return;
      const restrictions = getStepRestrictions(step, builderSelectedValueByStepOrder, activeConditionalRules, getBuilderStepByOrder);
      if (restrictions.hidden.has(selected)) hiddenToClear.push(step.id);
      else if (restrictions.disabled.has(selected)) disabledToClear.push(step.id);
    });
    const nextToClear = [...hiddenToClear, ...disabledToClear];
    if (!nextToClear.length) return;
    setBuilderSelections((prev) => {
      const next = { ...prev };
      nextToClear.forEach((stepId) => {
        delete next[stepId];
      });
      return next;
    });
    if (hiddenToClear.length > 0) {
      showAlert('One or more selections were cleared because they are hidden by active rules.', 'Rule Applied');
    }
  }, [
    mode,
    builderDefinition,
    builderSelections,
    builderSelectedValueByStepOrder,
    activeConditionalRules,
    getStepRestrictions,
    getBuilderStepByOrder,
    showAlert,
  ]);

  useEffect(() => {
    if (mode !== 'builder' || !builderDefinition || !builderCartEditMode) return;
    const selectedByOrder = {};
    (builderDefinition.steps || []).forEach((step, idx) => {
      selectedByOrder[Number(step.step_order || idx + 1)] = builderCartEditSelections?.[step.id] || '';
    });
    const hiddenToClear = [];
    const disabledToClear = [];
    (builderDefinition.steps || []).forEach((step) => {
      const selected = String(builderCartEditSelections?.[step.id] || '').trim();
      if (!selected) return;
      const restrictions = getStepRestrictions(step, selectedByOrder, activeConditionalRules, getBuilderStepByOrder);
      if (restrictions.hidden.has(selected)) hiddenToClear.push(step.id);
      else if (restrictions.disabled.has(selected)) disabledToClear.push(step.id);
    });
    const nextToClear = [...hiddenToClear, ...disabledToClear];
    if (!nextToClear.length) return;
    setBuilderCartEditSelections((prev) => {
      const next = { ...prev };
      nextToClear.forEach((stepId) => {
        delete next[stepId];
      });
      return next;
    });
    if (hiddenToClear.length > 0) {
      showAlert('Some cart edit selections were cleared because they are hidden by active rules.', 'Rule Applied');
    }
  }, [
    mode,
    builderDefinition,
    builderCartEditMode,
    builderCartEditSelections,
    activeConditionalRules,
    getStepRestrictions,
    getBuilderStepByOrder,
    showAlert,
  ]);

  if (mode === 'builder') {
    if (builderView === 'cart') {
      return (
        <div className="container-fluid py-3">
          <Card className="mb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <Typography.Title level={4} className="mb-0">
                  <ShoppingCart size={18} className="me-2" />
                  Configured Cart
                </Typography.Title>
                <Typography.Text type="secondary">
                  Review configured items for user <strong>{builderUserId || '—'}</strong>.
                </Typography.Text>
              </div>
              <Space>
                <Button onClick={() => navigate(`/products/configurable/use/${encodeURIComponent(routeProductCode || '')}`)}>
                  Back to Configurator
                </Button>
              </Space>
            </div>
          </Card>

          <Card className="mb-3">
            <Table
              size="small"
              rowKey="id"
              dataSource={builderCart}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Generated Part #',
                  key: 'generated_part_number',
                  render: (_, row) => (
                    <Button
                      type="link"
                      className="px-0"
                      onClick={() => openBuilderCartItem(row, false)}
                    >
                      {row.generated_part_number}
                    </Button>
                  ),
                },
                { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 90 },
                { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (value) => new Date(value).toLocaleString(), width: 220 },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 100,
                  render: (_, row) => (
                    <Button size="small" onClick={() => openBuilderCartItem(row, true)}>
                      Edit
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: 'No configured items in cart for this user.' }}
            />
          </Card>

          <Drawer
            open={builderCartDetailOpen}
            placement="right"
            width="50vw"
            title={`${builderCartEditMode ? 'Edit Configured Item' : 'Configured Item Details'}${(builderCartEditMode ? editedCartPartNumber : builderCartSelectedItem?.generated_part_number) ? ` - ${builderCartEditMode ? editedCartPartNumber : builderCartSelectedItem?.generated_part_number}` : ''}`}
            onClose={() => {
              setBuilderCartDetailOpen(false);
              setBuilderCartEditMode(false);
            }}
            extra={builderCartSelectedItem ? (
              <Space>
                {builderCartEditMode ? (
                  <>
                    <Button
                      onClick={() => {
                        setBuilderCartEditMode(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      loading={builderCartSavingEdit}
                      disabled={!canSaveCartEdits || cartEditRestrictedStepNames.length > 0}
                      onClick={handleSaveBuilderCartEdits}
                    >
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => openBuilderCartItem(builderCartSelectedItem, true)}>Edit</Button>
                )}
              </Space>
            ) : null}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <div
                  className="border rounded bg-light d-flex align-items-center justify-content-center"
                  style={{ width: '100%', height: BUILDER_MAIN_IMAGE_CANVAS_HEIGHT, overflow: 'hidden' }}
                >
                  <ImageWithFallback
                    src={builderDefinition?.main_image_url}
                    alt={builderDefinition?.name || builderDefinition?.product_code || 'Parent product'}
                    imageStyle={{ width: '100%', maxHeight: 320, objectFit: 'contain' }}
                    fallbackClassName="text-muted"
                  />
                </div>
              </Col>
              <Col xs={24}>
                <Typography.Title level={5} className="mb-1">
                  {builderDefinition?.name || builderDefinition?.product_code || 'Configurable Product'}
                </Typography.Title>
                <Typography.Text type="secondary" className="d-block mb-2">
                  Parent Product: <code>{builderDefinition?.product_code || '—'}</code>
                </Typography.Text>
                <RichHtmlBlock
                  html={builderDefinition?.description}
                  fallback="No description available."
                  className="text-secondary d-block mb-3"
                />

                <Typography.Text strong>{builderCartEditMode ? 'Update Your Configuration' : 'Your Configuration Choices'}</Typography.Text>
                <div className="mt-2 d-flex flex-column gap-2">
                  {builderCartEditMode ? (
                    <>
                      <div className="border rounded p-2">
                        <div className="small fw-semibold mb-2">Quantity</div>
                        <InputNumber
                          min={1}
                          value={builderCartEditQuantity}
                          onChange={(value) => setBuilderCartEditQuantity(Number(value || 1))}
                        />
                      </div>
                      {cartEditRestrictedStepNames.length > 0 ? (
                        <div className="small text-danger">
                          Update restricted selections before saving: {cartEditRestrictedStepNames.join(', ')}.
                        </div>
                      ) : null}
                      {(builderDefinition?.steps || []).map((step) => {
                        const isAttribute = String(step.selection_mode || 'product') === 'attribute';
                        const editSelectedByOrder = {};
                        (builderDefinition?.steps || []).forEach((item, idx) => {
                          const order = Number(item.step_order || idx + 1);
                          editSelectedByOrder[order] = builderCartEditSelections[item.id] || '';
                        });
                        const restrictions = getStepRestrictions(step, editSelectedByOrder, activeConditionalRules, getBuilderStepByOrder);
                        const options = (isAttribute
                          ? (step.attribute_values || []).map((item) => ({ label: item.value_label, value: item.value_label, identifier: item.identifier }))
                          : (step.products || []).map((item) => ({
                            label: item.product?.name ? `${item.product.name} (${item.product_code})` : item.product_code,
                            value: item.product_code,
                            identifier: item.identifier,
                          })))
                          .filter((item) => !restrictions.hidden.has(String(item.value || '')))
                          .map((item) => {
                            const key = String(item.value || '');
                            const reasons = restrictions.disabledReasons.get(key) || [];
                            const reasonText = reasons.length ? reasons.join('; ') : 'Disabled by prior selections';
                            return restrictions.disabled.has(key)
                              ? { ...item, label: `${item.label} (${reasonText})`, disabled: true }
                              : item;
                          });
                        const selectedValue = builderCartEditSelections[step.id];
                        const selectedOption = options.find((item) => item.value === selectedValue);
                        return (
                          <div key={step.id} className="border rounded p-2">
                            <div className="small fw-semibold mb-2">{step.step_name}</div>
                            <Select
                              className="w-100"
                              value={selectedValue}
                              options={options}
                              placeholder={`Select ${isAttribute ? (step.attribute_name || 'value') : 'product'}`}
                              onChange={(value) => {
                                setBuilderCartEditSelections((prev) => ({ ...prev, [step.id]: value }));
                              }}
                            />
                            <div className="mt-2 d-flex justify-content-between align-items-center">
                              <Typography.Text type="secondary" className="small">
                                {isAttribute ? `${step.attribute_name || 'Attribute'}:` : 'Product'}
                              </Typography.Text>
                              <Tag color="green">{selectedOption?.identifier || '—'}</Tag>
                            </div>
                          </div>
                        );
                      })}
                      <div className="border rounded p-2 bg-light">
                        <Typography.Text strong>Updated Part Number</Typography.Text>
                        <div className="mt-1">
                          <Tag color="blue">{editedCartPartNumber || '—'}</Tag>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {selectedCartItemSelectionRows.map((item, idx) => (
                        <div key={`${item?.step_id || idx}-${idx}`} className="border rounded p-2 d-flex justify-content-between align-items-center">
                          <div>
                            <div className="small fw-semibold">
                              {item?.attribute_name
                                ? `${item.attribute_name}: ${item?.attribute_value || '—'}`
                                : (item?.product_code || 'Selected product')}
                            </div>
                            {item?.attribute_name ? null : (
                              <div className="small text-muted">Product selection</div>
                            )}
                          </div>
                          <Tag color="green">{item?.identifier || '—'}</Tag>
                        </div>
                      ))}
                      {selectedCartItemSelectionRows.length === 0 ? (
                        <Typography.Text type="secondary">No selections found.</Typography.Text>
                      ) : null}
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Drawer>
        </div>
      );
    }

    return (
      <div className="container-fluid py-3">
        <Card className="mb-3">
          <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
            <Space direction="vertical" size={6}>
              <Typography.Title level={4} className="mb-0">
                <Wrench size={18} className="me-2" />
                Configure Product
              </Typography.Title>
              <Typography.Text type="secondary">
                Complete each step to build the configured part number.
              </Typography.Text>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Tag color="blue">
                  Parent Product ID: {builderDefinition?.product_code || routeProductCode || '—'} ({builderDefinition?.include_parent_product_code !== false ? 'included' : 'excluded'})
                </Tag>
                <Tag color="green">Built Part #: {builtPartNumber || '—'}</Tag>
                <Tag color={completedBuilderSteps === (builderDefinition?.steps || []).length ? 'success' : 'warning'}>
                  Steps Completed: {completedBuilderSteps}/{(builderDefinition?.steps || []).length}
                </Tag>
              </div>
            </Space>
            <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
              <Button
                size="small"
                icon={<ShoppingCart size={14} />}
                onClick={() => navigate(`/products/configurable/use/${encodeURIComponent(routeProductCode || '')}/cart`)}
              >
                Cart ({builderCart.length})
              </Button>
              <span className="d-inline-flex align-items-center gap-1 fw-semibold">
                <User size={16} />
                User
              </span>
              {builderUserEditMode ? (
                <>
                  <Input
                    value={builderUserDraft}
                    onChange={(e) => setBuilderUserDraft(e.target.value)}
                    placeholder="Enter user id"
                    style={{ width: 260, maxWidth: '100%' }}
                    onPressEnter={() => {
                      const value = String(builderUserDraft || '').trim();
                      if (!value) return;
                      setBuilderUserId(value);
                      try {
                        localStorage.setItem(builderUserStorageKey, value);
                      } catch (err) {
                        // ignore storage errors
                      }
                      setBuilderUserEditMode(false);
                    }}
                  />
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      const value = String(builderUserDraft || '').trim();
                      if (!value) {
                        showAlert('Enter a user id first.', 'Validation');
                        return;
                      }
                      setBuilderUserId(value);
                      try {
                        localStorage.setItem(builderUserStorageKey, value);
                      } catch (err) {
                        // ignore storage errors
                      }
                      setBuilderUserEditMode(false);
                    }}
                  >
                    Save User
                  </Button>
                </>
              ) : (
                <>
                  <Tag color="processing">{builderUserId || '—'}</Tag>
                  <Button size="small" onClick={() => setBuilderUserEditMode(true)}>
                    Change
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

        {!builderDefinition ? (
          <Card>
            <Typography.Text type="secondary">
              No configurable definition found for this product.
            </Typography.Text>
          </Card>
        ) : (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={24} xl={16}>
                <Row gutter={[12, 12]}>
                  <Col xs={24}>
                    <Card>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={10}>
                          <div
                            className="border rounded bg-light d-flex align-items-center justify-content-center"
                            style={{ width: '100%', height: BUILDER_MAIN_IMAGE_CANVAS_HEIGHT, overflow: 'hidden', cursor: builderActiveImage ? 'zoom-in' : 'default' }}
                            onClick={() => {
                              if (!builderActiveImage) return;
                              setBuilderZoomImage(builderActiveImage);
                              setBuilderZoomScale(1);
                              setBuilderZoomOpen(true);
                            }}
                          >
                            <ImageWithFallback
                              src={builderActiveImage}
                              alt={builderDefinition?.name || builderDefinition?.product_code || 'Configurable Product'}
                              imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              fallbackText="No product image"
                              fallbackClassName="text-muted"
                            />
                          </div>
                          {builderGalleryImages.length > 1 ? (
                            <div className="d-flex gap-2 mt-2 flex-nowrap overflow-auto pb-1">
                              {builderGalleryImages.map((img) => (
                                <button
                                  key={img}
                                  type="button"
                                  className={`border rounded bg-white p-1 ${builderActiveImage === img ? 'border-primary' : ''}`}
                                  onClick={() => {
                                    setBuilderActiveImage(img);
                                    setBuilderZoomImage(img);
                                    setBuilderZoomScale(1);
                                    setBuilderZoomOpen(true);
                                  }}
                                  style={{ width: BUILDER_THUMB_CANVAS_SIZE, height: BUILDER_THUMB_CANVAS_SIZE, flex: '0 0 auto' }}
                                >
                                  <ImageWithFallback
                                    src={img}
                                    alt="thumb"
                                    imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                  />
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </Col>
                        <Col xs={24} md={14}>
                          <Typography.Title level={4} className="mb-1">
                            {builderDefinition?.name || builderDefinition?.product_code}
                          </Typography.Title>
                          <RichHtmlBlock
                            html={builderDefinition?.description}
                            fallback="Configure this product by selecting options in each step."
                            className="text-secondary d-block mb-2"
                          />
                          <div className="mb-2">
                            <Tag color="blue"><code>{builderDefinition?.product_code}</code></Tag>
                          </div>
                          <Typography.Text strong>Built Part Number</Typography.Text>
                          <div className="d-flex flex-wrap gap-2 mt-1 mb-2">
                            {builtPartSegments.length > 0 ? (
                              builtPartSegments.map((part, idx) => (
                                <span
                                  key={`part-${idx}-${part}`}
                                  className="border rounded px-2 py-1 bg-white fw-semibold"
                                >
                                  {part}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted">Select all options</span>
                            )}
                          </div>
                          <Typography.Text type="secondary">
                            Choose one option in each step below. Your final configured part number updates instantly.
                          </Typography.Text>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                  {(() => {
                    const steps = builderDefinition.steps || [];
                    const step = steps[currentBuilderStepIdx];
                    if (!step) return null;
                    const canGoPrev = currentBuilderStepIdx > 0;
                    const canGoNext = currentBuilderStepIdx < steps.length - 1;
                    return (
                    <Col xs={24} key={step.id}>
                      <Card
                        ref={builderStepCardRef}
                        title={`Step ${currentBuilderStepIdx + 1} of ${steps.length}: ${step.step_name}`}
                        extra={
                          String(step.selection_mode || 'product') === 'attribute'
                            ? <Tag color="purple">{step.attribute_name || 'Attribute'}:</Tag>
                            : <Tag color="blue">Product</Tag>
                        }
                      >
                        <div className="d-flex justify-content-end gap-2 mb-2">
                          <Button
                            size="small"
                            disabled={!canGoPrev}
                            onClick={() => setCurrentBuilderStepIdx((prev) => getNextNonAutoStepIndex(prev - 1, -1))}
                          >
                            Previous
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            disabled={!canGoNext}
                            onClick={() => setCurrentBuilderStepIdx((prev) => getNextNonAutoStepIndex(prev + 1, 1))}
                          >
                            Next
                          </Button>
                        </div>
                        {(() => {
                          const restrictions = getStepRestrictions(step, builderSelectedValueByStepOrder, activeConditionalRules, getBuilderStepByOrder);
                          const attributeOptions = (step.attribute_values || []).filter(
                            (option) => !restrictions.hidden.has(String(option?.value_label || '')),
                          );
                          const productOptions = (step.products || []).filter(
                            (option) => !restrictions.hidden.has(String(option?.product_code || '')),
                          );
                          return (
                        <div className="d-flex flex-column gap-2">
                          {String(step.selection_mode || 'product') === 'attribute'
                            ? attributeOptions.map((option) => {
                              const optionValue = String(option.value_label || '');
                              const disabledByRule = restrictions.disabled.has(optionValue);
                              const disabledReason = (restrictions.disabledReasons.get(optionValue) || []).join('; ') || 'Disabled by prior selections';
                              return (
                              <label
                                key={option.value_label}
                                className={`border rounded p-2 d-flex justify-content-between align-items-center ${
                                  builderSelections[step.id] === option.value_label ? 'border-primary bg-light' : ''
                                }`}
                              >
                                <span>
                                  <input
                                    type="radio"
                                    name={`step-${step.id}`}
                                    checked={builderSelections[step.id] === option.value_label && !disabledByRule}
                                    disabled={disabledByRule}
                                    onChange={() => handleBuilderStepSelection(step, option.value_label)}
                                  />
                                  <span className={`ms-2 fw-semibold ${disabledByRule ? 'text-muted' : ''}`}>
                                    {option.value_label}
                                  </span>
                                </span>
                                <div className="d-flex align-items-center gap-1">
                                  {disabledByRule ? <Tag color="default">{disabledReason}</Tag> : null}
                                  <Tag>{option.identifier}</Tag>
                                </div>
                              </label>
                              );
                            })
                            : productOptions.map((option) => {
                              const optionValue = String(option.product_code || '');
                              const disabledByRule = restrictions.disabled.has(optionValue);
                              const disabledReason = (restrictions.disabledReasons.get(optionValue) || []).join('; ') || 'Disabled by prior selections';
                              return (
                              <div
                                key={option.product_code}
                                className={`border rounded p-2 d-flex justify-content-between align-items-center gap-2 ${
                                  builderSelections[step.id] === option.product_code ? 'border-primary bg-light' : ''
                                }`}
                              >
                                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                  <input
                                    type="radio"
                                    name={`step-${step.id}`}
                                    checked={builderSelections[step.id] === option.product_code && !disabledByRule}
                                    disabled={disabledByRule}
                                    onChange={() => handleBuilderStepSelection(step, option.product_code)}
                                  />
                                  <div
                                    className="border rounded bg-light d-flex align-items-center justify-content-center"
                                    style={{ width: 58, height: 58, flexShrink: 0, overflow: 'hidden' }}
                                  >
                                    <ImageWithFallback
                                      src={option.product?.image_url}
                                      alt={option.product?.name || option.product_code}
                                      imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <div className="fw-semibold text-truncate">
                                      {option.product?.name || option.product_code}
                                    </div>
                                    <div className="small text-muted">
                                      <code>{option.product_code}</code>
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  {disabledByRule ? <Tag color="default">{disabledReason}</Tag> : null}
                                  <Button
                                    size="small"
                                    icon={<Eye size={13} />}
                                    onClick={() => openProductDetails(option.product, option.product_code)}
                                  >
                                    Details
                                  </Button>
                                  <Tag>{option.identifier}</Tag>
                                </div>
                              </div>
                              );
                            })}
                        </div>
                          );
                        })()}
                      </Card>
                    </Col>
                    );
                  })()}
                </Row>
              </Col>

              <Col xs={24} xl={8}>
                <Card title="Configuration Summary" style={{ position: 'sticky', top: 84 }}>
                  <div className="mb-2">
                    <div className="small text-muted">Generated Part Number</div>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {builtPartSegments.length > 0 ? (
                        builtPartSegments.map((part, idx) => (
                          <span key={`summary-part-${idx}-${part}`} className="border rounded px-2 py-1 bg-white small fw-semibold">
                            {part}
                          </span>
                        ))
                      ) : (
                        <span className="text-muted small">Select all steps to generate part number</span>
                      )}
                    </div>
                  </div>
                  <div className="mb-3">
                    {builderStepSummary.map((item, idx) => (
                      <div key={item.stepId} className="d-flex justify-content-between align-items-start border-bottom py-2">
                        <div className="me-2">
                          <div className="small fw-semibold">Step {idx + 1}: {item.stepName}</div>
                          <div className="small text-muted">{item.label}</div>
                        </div>
                        {item.completed ? (
                          <div className="d-flex flex-column align-items-end gap-1">
                            <Tag color="green">{item.identifier || 'Done'}</Tag>
                            {item.autoSelected ? <Tag color="blue">Auto-selected</Tag> : null}
                          </div>
                        ) : <Tag>Pending</Tag>}
                      </div>
                    ))}
                  </div>
                  <Row gutter={[8, 8]}>
                    <Col xs={24}>
                      <Form.Item label="User ID (saved at top)" className="mb-2">
                        <Input value={builderUserId} disabled placeholder="Set user ID above" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Quantity" className="mb-2">
                        <InputNumber min={1} value={builderQuantity} onChange={(value) => setBuilderQuantity(Number(value || 1))} className="w-100" />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Button
                        type="primary"
                        className="w-100"
                        icon={<ShoppingCart size={15} />}
                        disabled={!canAddToCart || builderRestrictedStepNames.length > 0}
                        onClick={addToCart}
                      >
                        Add Configured Item to Cart
                      </Button>
                    </Col>
                  </Row>
                  {!canAddToCart ? (
                    <div className="small text-muted mt-2">
                      Complete all step selections and enter user ID to enable Add to Cart.
                    </div>
                  ) : null}
                  {builderRestrictedStepNames.length > 0 ? (
                    <div className="small text-danger mt-2">
                      Update restricted selections before adding to cart: {builderRestrictedStepNames.join(', ')}.
                    </div>
                  ) : null}
                </Card>
              </Col>
            </Row>

            {builderCart.length > 0 && (
              <Card className="mt-3" title="Recently Added to Cart">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={builderCart}
                  columns={[
                    { title: 'Generated Part #', dataIndex: 'generated_part_number', key: 'generated_part_number' },
                    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 100 },
                    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (value) => new Date(value).toLocaleString() },
                  ]}
                />
              </Card>
            )}

            <Modal
              open={appAlert.open}
              title={appAlert.title || 'Notice'}
              onOk={() => setAppAlert({ open: false, title: 'Notice', message: '' })}
              onCancel={() => setAppAlert({ open: false, title: 'Notice', message: '' })}
              cancelButtonProps={{ style: { display: 'none' } }}
            >
              <Typography.Text>{appAlert.message}</Typography.Text>
            </Modal>

            <Modal
              open={builderZoomOpen}
              title="Image Zoom"
              footer={null}
              width={920}
              onCancel={() => {
                setBuilderZoomOpen(false);
                setBuilderZoomScale(1);
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Typography.Text type="secondary">Zoom</Typography.Text>
                <div className="d-flex align-items-center gap-2">
                  <Button size="small" onClick={() => setBuilderZoomScale((prev) => Math.max(1, Number((prev - 0.25).toFixed(2))))}>-</Button>
                  <span className="small">{Math.round(builderZoomScale * 100)}%</span>
                  <Button size="small" onClick={() => setBuilderZoomScale((prev) => Math.min(4, Number((prev + 0.25).toFixed(2))))}>+</Button>
                </div>
              </div>
              {builderGalleryImages.length > 1 ? (
                <div className="d-flex gap-2 flex-nowrap overflow-auto pb-2 mb-2 border-bottom">
                  {builderGalleryImages.map((img) => (
                    <button
                      key={`builder-zoom-inline-${img}`}
                      type="button"
                      className={`border rounded bg-white p-1 ${(builderZoomImage || builderActiveImage) === img ? 'border-primary' : ''}`}
                      style={{ width: BUILDER_THUMB_CANVAS_SIZE, height: BUILDER_THUMB_CANVAS_SIZE, flex: '0 0 auto' }}
                      onClick={() => setBuilderZoomImage(img)}
                    >
                      <ImageWithFallback
                        src={img}
                        alt="builder-zoom-thumb"
                        imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="border rounded bg-light d-flex align-items-center justify-content-center" style={{ minHeight: 520, overflow: 'auto' }}>
                <ImageWithFallback
                  src={builderZoomImage || builderActiveImage}
                  alt="zoom"
                  imageStyle={{
                    transform: `scale(${builderZoomScale})`,
                    transformOrigin: 'center center',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                  fallbackClassName="text-muted"
                />
              </div>
            </Modal>

            <Modal
              open={detailModal.open}
              title="Product Details"
              footer={null}
              onCancel={() => setDetailModal({ open: false, product: null })}
            >
              <div className="d-flex gap-3">
                <div
                  className="border rounded bg-light d-flex align-items-center justify-content-center"
                  style={{ width: 120, height: 120, flexShrink: 0, overflow: 'hidden' }}
                >
                  <ImageWithFallback
                    src={detailModal.product?.imageUrl}
                    alt={detailModal.product?.name || detailModal.product?.productCode}
                    imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
                <div className="small">
                  <div><strong>Code:</strong> {detailModal.product?.productCode || '—'}</div>
                  <div><strong>Name:</strong> {detailModal.product?.name || '—'}</div>
                  <div><strong>Category:</strong> {detailModal.product?.category || '—'}</div>
                  <div><strong>Manufacturer:</strong> {detailModal.product?.manufacturerName || '—'}</div>
                  <div><strong>MPN:</strong> {detailModal.product?.manufacturerPartNumber || '—'}</div>
                  <div className="mt-2 text-muted">
                    {detailModal.product?.description || 'No description available.'}
                  </div>
                </div>
              </div>
            </Modal>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <Card className="mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Space>
            <Settings2 size={18} />
            <Typography.Title level={4} className="mb-0">
              {editor.id ? 'Edit Configurable Product' : 'Add Configurable Product'}
            </Typography.Title>
          </Space>
          <Space>
            <Button onClick={() => navigate('/products/configurable')}>Back to List</Button>
            {(editor.published_product_code || editor.product_code) ? (
              <Button
                disabled={!editor.has_published_version || editor.is_active === false}
                onClick={() => navigate(`/products/configurable/use/${encodeURIComponent(editor.published_product_code || editor.product_code)}`)}
              >
                Open Builder
              </Button>
            ) : null}
            {editor.id ? <Button danger onClick={removeDefinition}>Delete</Button> : null}
          </Space>
        </div>
      </Card>

      <Card title="Definition Editor" loading={loading}>
            <div className="mb-3 border rounded bg-light p-2">
              <Row gutter={[12, 12]} align="middle">
                <Col xs={24} sm={6} md={4}>
                  <div
                    className="border rounded bg-white d-flex align-items-center justify-content-center"
                    style={{ width: '100%', height: 82, overflow: 'hidden' }}
                  >
                    <ImageWithFallback
                      src={editorPreviewImage || editor.main_image_url || editorGalleryImages[0]}
                      alt={editor.name || editor.product_code || 'Configurable product'}
                      imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      fallbackClassName="text-muted"
                    />
                  </div>
                </Col>
                <Col xs={24} sm={18} md={20}>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Tag color="blue"><code>{editor.product_code || '—'}</code></Tag>
                    <Typography.Text strong>{editor.name || 'Untitled configurable product'}</Typography.Text>
                    <Tag color={editor.workflow_state === 'published' ? 'success' : 'gold'}>
                      {editor.workflow_state === 'published' ? 'Published' : 'Draft'}
                    </Tag>
                    <Tag color={editor.is_ready ? 'blue' : 'default'}>
                      {editor.is_ready ? 'Ready' : 'Not Ready'}
                    </Tag>
                  </div>
                  <div className="small text-muted mt-1">
                    Editing product context for definition and step configuration.
                  </div>
                </Col>
              </Row>
            </div>

            <Tabs
              className="mb-3"
              type="card"
              activeKey={activeEditorWorkspaceTab}
              onChange={setActiveEditorWorkspaceTab}
              items={[
                { key: 'productInfo', label: 'Product Information' },
                { key: 'steps', label: `Configuration Steps (${(editor.steps || []).length})` },
                { key: 'rules', label: `Conditions & Rules (${(editor.conditional_rules || []).length})` },
                { key: 'preview', label: 'Preview' },
              ]}
            />

            {activeEditorWorkspaceTab === 'productInfo' ? (
            <Row gutter={[12, 12]}>
              <Col xs={24} xl={15}>
                <Card
                  size="small"
                  className="h-100"
                  title="Product Definition"
                  extra={(
                    <Space size={6}>
                      <Tag color={editor.workflow_state === 'published' ? 'success' : 'gold'}>
                        {editor.workflow_state === 'published' ? 'Published' : 'Draft'}
                      </Tag>
                      <Tag color={editor.is_ready ? 'blue' : 'default'}>
                        {editor.is_ready ? 'Ready' : 'Not Ready'}
                      </Tag>
                    </Space>
                  )}
                >
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Base Product Code" required className="mb-2">
                        <Input value={editor.product_code} onChange={(e) => setEditor((prev) => ({ ...prev, product_code: e.target.value }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Display Name" required className="mb-2">
                        <Input value={editor.name} onChange={(e) => setEditor((prev) => ({ ...prev, name: e.target.value }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label="Workflow" className="mb-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Tag color={editor.workflow_state === 'published' ? 'success' : 'gold'}>
                            {editor.workflow_state === 'published' ? 'Published' : 'Draft'}
                          </Tag>
                          <Tag color={editor.is_ready ? 'blue' : 'default'}>
                            {editor.is_ready ? 'Ready to Publish' : 'Needs Setup'}
                          </Tag>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item label="Part Number Generation" className="mb-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Switch
                            checked={editor.include_parent_product_code !== false}
                            onChange={(value) => setEditor((prev) => ({ ...prev, include_parent_product_code: value }))}
                          />
                          <Typography.Text>
                            Include parent product ID (`{editor.product_code || 'BASE'}`) in generated part number
                          </Typography.Text>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Description" className="mb-0">
                        <HtmlRichTextEditor
                          value={editor.description || ''}
                          onChange={(value) => setEditor((prev) => ({ ...prev, description: String(value || '') }))}
                          placeholder="Enter product description with rich formatting"
                          modules={descriptionEditorModules}
                          formats={descriptionEditorFormats}
                          minHeight={260}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col xs={24} xl={9}>
                <Card
                  size="small"
                  title="Image Library & Preview"
                  extra={<Tag color="blue">{(editor.image_urls || []).length} image(s)</Tag>}
                  style={{ height: '100%' }}
                >
                  <div
                    className="border rounded bg-light d-flex align-items-center justify-content-center mb-2"
                    style={{ width: '100%', minHeight: 260, overflow: 'hidden', cursor: editorPreviewImage ? 'zoom-in' : 'default' }}
                    onClick={() => {
                      if (!editorPreviewImage) return;
                      setEditorZoomImage(editorPreviewImage);
                      setEditorZoomOpen(true);
                      setEditorZoomScale(1);
                    }}
                  >
                    <ImageWithFallback
                      src={editorPreviewImage}
                      alt={editor.name || editor.product_code || 'Configurable product preview'}
                      imageStyle={{ width: '100%', maxHeight: 300, objectFit: 'contain' }}
                      fallbackText="No image"
                      fallbackClassName="text-muted"
                    />
                  </div>
                  {editorGalleryImages.length > 1 ? (
                    <div className="d-flex gap-2 mb-2 flex-nowrap overflow-auto pb-1">
                      {editorGalleryImages.map((img) => (
                        <button
                          key={img}
                          type="button"
                          className={`border rounded bg-white p-1 ${editorPreviewImage === img ? 'border-primary' : ''}`}
                          onClick={() => {
                            setEditorPreviewImage(img);
                            setEditorZoomImage(img);
                            setEditorZoomOpen(true);
                            setEditorZoomScale(1);
                          }}
                          style={{ width: 68, height: 68, flex: '0 0 auto' }}
                          title="View full-size image"
                        >
                          <ImageWithFallback
                            src={img}
                            alt="editor-thumb"
                            imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="small text-muted mb-2">
                    Previewing {editorPreviewImage ? <code>{editorPreviewImage}</code> : 'no image selected'}.
                    {editor.main_image_url ? <> Main image: <code>{editor.main_image_url}</code>.</> : null}
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {(editor.image_urls || []).map((img, idx) => (
                      <div key={`img-${idx}`} className="d-flex align-items-center gap-2">
                        <input
                          type="radio"
                          checked={(editor.main_image_url || '') === img}
                          onChange={() => {
                            setEditor((prev) => ({ ...prev, main_image_url: img }));
                            setEditorPreviewImage(img);
                          }}
                          title="Set as main image"
                        />
                        <Input
                          value={img}
                          placeholder="https://...image.jpg"
                          onFocus={() => setEditorPreviewImage(String(img || '').trim())}
                          onChange={(e) =>
                            setEditor((prev) => {
                              const urls = [...(prev.image_urls || [])];
                              const oldVal = urls[idx];
                              urls[idx] = e.target.value;
                              const nextMain = (prev.main_image_url || '') === oldVal ? e.target.value : prev.main_image_url;
                              return { ...prev, image_urls: urls, main_image_url: nextMain };
                            })
                          }
                        />
                        <Button
                          onClick={() => setEditorPreviewImage(String(img || '').trim())}
                          disabled={!String(img || '').trim()}
                        >
                          Preview
                        </Button>
                        <Button
                          danger
                          onClick={() =>
                            setEditor((prev) => {
                              const urls = [...(prev.image_urls || [])];
                              const removed = urls[idx];
                              urls.splice(idx, 1);
                              const nextMain = (prev.main_image_url || '') === removed ? (urls[0] || '') : prev.main_image_url;
                              if ((editorPreviewImage || '') === removed) {
                                setEditorPreviewImage(urls[0] || '');
                              }
                              return { ...prev, image_urls: urls, main_image_url: nextMain };
                            })
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        onClick={() =>
                          setEditor((prev) => ({
                            ...prev,
                            image_urls: [...(prev.image_urls || []), ''],
                          }))
                        }
                      >
                        Add Image URL
                      </Button>
                      <Typography.Text type="secondary">
                        Use radio to mark the main image used in builder and cart views.
                      </Typography.Text>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col xs={24}>
                <Card size="small" className="mb-2">
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                      <div className="border rounded p-2 h-100 bg-light">
                        <div className="small text-muted">Product Code</div>
                        <div className="fw-semibold">{editor.product_code || '—'}</div>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div className="border rounded p-2 h-100 bg-light">
                        <div className="small text-muted">Steps Defined</div>
                        <div className="fw-semibold">{(editor.steps || []).length}</div>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div className="border rounded p-2 h-100 bg-light">
                        <div className="small text-muted">Main Image Set</div>
                        <div className="fw-semibold">{editor.main_image_url ? 'Yes' : 'No'}</div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
            ) : null}

            {activeEditorWorkspaceTab === 'preview' ? (
            <Row gutter={[12, 12]}>
              <Col xs={24}>
                <Card size="small" title="Builder Preview Snapshot">
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                      <div
                        className="border rounded bg-light d-flex align-items-center justify-content-center"
                        style={{ width: '100%', minHeight: 220, overflow: 'hidden' }}
                      >
                        <ImageWithFallback
                          src={editorPreviewImage || editor.main_image_url}
                          alt={editor.name || editor.product_code || 'Preview'}
                          imageStyle={{ width: '100%', maxHeight: 240, objectFit: 'contain' }}
                          fallbackClassName="text-muted"
                        />
                      </div>
                    </Col>
                    <Col xs={24} md={16}>
                      <Typography.Title level={5} className="mb-1">
                        {editor.name || 'Configurable product name'}
                      </Typography.Title>
                      <Typography.Text type="secondary" className="d-block mb-2">
                        Product Code: <code>{editor.product_code || '—'}</code>
                      </Typography.Text>
                      <RichHtmlBlock
                        html={editor.description}
                        fallback="Add product description to see preview."
                        className="text-secondary d-block mb-2"
                      />
                      <div className="d-flex flex-wrap gap-2">
                        {(editor.steps || []).slice(0, 6).map((step, idx) => (
                          <Tag key={`editor-preview-step-${idx}`} color={step.step_name ? 'blue' : 'default'}>
                            {step.step_name || `Step ${idx + 1}`}
                          </Tag>
                        ))}
                        {(editor.steps || []).length > 6 ? <Tag>+{(editor.steps || []).length - 6} more</Tag> : null}
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
            ) : null}

            {activeEditorWorkspaceTab === 'rules' ? (
            <Card
              size="small"
              title="Conditional Rules"
              extra={(
                <Space>
                  <Button size="small" type="link" onClick={() => setRulesHelpOpen(true)}>
                    How rules work
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      const defaultStepOrder = Number(editor.steps?.[0]?.step_order || 1);
                      setEditor((prev) => {
                        const nextRules = [
                          ...(prev.conditional_rules || []),
                          { ...blankConditionalRule(), target_step_order: defaultStepOrder },
                        ];
                        setActiveRuleEditorIdx(nextRules.length - 1);
                        setRuleEditorDrawerOpen(true);
                        return { ...prev, conditional_rules: nextRules };
                      });
                    }}
                  >
                    Add Guided Rule
                  </Button>
                </Space>
              )}
            >
              <Alert
                type="info"
                showIcon
                className="mb-3"
                message="Guided rule builder"
                description="Work top-to-bottom for each rule: choose what to restrict (Then), then define when it should trigger (When)."
              />
              <Card size="small" className="mb-3" style={{ background: '#f6faff', borderColor: '#cce3ff' }}>
                <Typography.Text strong className="d-block mb-1">Build rules as: If selections match (When) then restrict options (Then)</Typography.Text>
                <Typography.Text type="secondary" className="d-block">
                  `hide` removes options from the builder. `disable` keeps options visible but not selectable.
                </Typography.Text>
                <Typography.Text type="secondary" className="d-block mt-1">
                  Easier mental model: pick the source step/value first, then choose unavailable options in another step.
                </Typography.Text>
                <Button type="link" size="small" className="px-0 mt-1" onClick={() => setRulesHelpOpen(true)}>
                  Open full guide and examples
                </Button>
              </Card>
              {(editor.conditional_rules || []).length === 0 ? (
                <Card size="small">
                  <Typography.Text type="secondary">No rules added yet. Click "Add Rule" to create one.</Typography.Text>
                </Card>
              ) : null}
              <div className="d-flex flex-column gap-3">
                {(editor.conditional_rules || []).map((rule, ruleIdx) => {
                  const guidance = getRuleGuidanceSnapshot(rule, getEditorStepByOrder, getStepOptionChoices);
                  const plainRuleExplanation = buildRulePlainLanguageExplanation(rule, getEditorStepByOrder, getStepOptionChoices);
                  return (
                    <Card
                      key={rule.id || `rule-${ruleIdx}`}
                      size="small"
                      title={rule.name || `Rule ${ruleIdx + 1}`}
                      extra={(
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              setActiveRuleEditorIdx(ruleIdx);
                              setRuleEditorDrawerOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Switch
                            checked={rule.enabled !== false}
                            onChange={(checked) => {
                              setEditor((prev) => {
                                const nextRules = [...(prev.conditional_rules || [])];
                                nextRules[ruleIdx] = { ...nextRules[ruleIdx], enabled: checked };
                                return { ...prev, conditional_rules: nextRules };
                              });
                            }}
                          />
                          <Button
                            danger
                            size="small"
                            onClick={() => {
                              setEditor((prev) => ({
                                ...prev,
                                conditional_rules: (prev.conditional_rules || []).filter((_, idx) => idx !== ruleIdx),
                              }));
                              if (activeRuleEditorIdx === ruleIdx) {
                                setRuleEditorDrawerOpen(false);
                                setActiveRuleEditorIdx(null);
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </Space>
                      )}
                    >
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <Tag color={rule.enabled !== false ? 'green' : 'default'}>
                          {rule.enabled !== false ? 'Enabled' : 'Disabled'}
                        </Tag>
                        <Tag color={rule.effect === 'disable' ? 'gold' : 'blue'}>
                          {rule.effect === 'disable' ? 'Disable' : 'Hide'}
                        </Tag>
                        <Tag>
                          Target: {guidance.targetStepLabel}
                        </Tag>
                        <Tag>
                          Groups: {(rule.groups || []).length}
                        </Tag>
                        <Tag>
                          Complete: {guidance.doneCount}/{guidance.checklist.length}
                        </Tag>
                      </div>
                      <Typography.Text type="secondary" className="small d-block mt-2">
                        {plainRuleExplanation}
                      </Typography.Text>
                    </Card>
                  );
                })}
              </div>
            </Card>
            ) : null}

            {activeEditorWorkspaceTab === 'steps' ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Typography.Text strong>Configuration Steps</Typography.Text>
                <Button
                  size="small"
                  icon={<Plus size={13} />}
                  onClick={() => {
                    setEditor((prev) => ({
                      ...prev,
                      steps: [...(prev.steps || []), blankStep((prev.steps || []).length)],
                    }));
                    setActiveEditorStepKey(String((editor.steps || []).length));
                  }}
                >
                  Add Step
                </Button>
              </div>

              <Tabs
                className="mb-2"
                type="card"
                activeKey={activeEditorStepKey}
                onChange={setActiveEditorStepKey}
                items={(editor.steps || []).map((step, idx) => ({
                  key: String(idx),
                  label: step.step_name ? `Step ${idx + 1}: ${step.step_name}` : `Step ${idx + 1}`,
                }))}
              />

              {(() => {
                const steps = editor.steps || [];
                if (!steps.length) return null;
                const stepIndex = Math.min(Math.max(Number(activeEditorStepKey) || 0, 0), steps.length - 1);
                const step = steps[stepIndex];
                return (
                <Card key={`step-${stepIndex}`} className="mb-2" size="small" title={`Step ${stepIndex + 1}`}>
                <Row gutter={[8, 8]}>
                  <Col xs={24} md={6}>
                    <div className="border rounded h-100 px-2 py-1 d-flex align-items-center justify-content-between bg-light">
                      <Typography.Text type="secondary" className="small">Step Order</Typography.Text>
                      <Tag color="blue" className="mb-0">Step {Number(step.step_order || stepIndex + 1)}</Tag>
                    </div>
                  </Col>
                  <Col xs={24} md={14}>
                    <Input
                      value={step.step_name}
                      placeholder="Step name (e.g. Capacity)"
                      onChange={(e) =>
                        setEditor((prev) => {
                          const steps = [...(prev.steps || [])];
                          steps[stepIndex] = { ...steps[stepIndex], step_name: e.target.value };
                          return { ...prev, steps };
                        })
                      }
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <Select
                      className="w-100"
                      value={step.selection_mode || 'product'}
                      options={[
                        { value: 'product', label: 'Select Product' },
                        { value: 'attribute', label: 'Select Attribute Value' },
                      ]}
                      onChange={(value) =>
                        setEditor((prev) => {
                          const steps = [...(prev.steps || [])];
                          const current = { ...(steps[stepIndex] || blankStep(stepIndex)) };
                          current.selection_mode = value;
                          steps[stepIndex] = current;
                          return { ...prev, steps };
                        })
                      }
                    />
                  </Col>
                  <Col xs={24} md={10}>
                    {step.selection_mode === 'attribute' ? (
                      <Input
                        value={step.attribute_name || ''}
                        placeholder="Attribute Name (e.g. Color, Voltage)"
                        onChange={(e) =>
                          setEditor((prev) => {
                            const steps = [...(prev.steps || [])];
                            steps[stepIndex] = { ...steps[stepIndex], attribute_name: e.target.value };
                            return { ...prev, steps };
                          })
                        }
                      />
                    ) : null}
                  </Col>
                  <Col xs={24} md={4}>
                    <Button
                      danger
                      className="w-100"
                      onClick={() =>
                        setEditor((prev) => ({
                          ...prev,
                          steps: (prev.steps || []).filter((_, idx) => idx !== stepIndex).map((item, idx) => ({
                            ...item,
                            step_order: idx + 1,
                          })),
                        }))
                      }
                    >
                      Remove
                    </Button>
                  </Col>

                  <Col xs={24}>
                    <Tabs
                      type="card"
                      items={[
                        {
                          key: 'selected',
                          label: step.selection_mode === 'attribute' ? 'Selected Attributes' : 'Selected Products',
                          children: step.selection_mode === 'attribute' ? (
                            <div className="d-flex flex-column gap-2">
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="small text-muted">
                                  Define selectable values for attribute <strong>{step.attribute_name || '—'}</strong> and assign part identifiers.
                                </div>
                                <div className="d-flex gap-2">
                                  <Button size="small" onClick={() => pasteAttributeValuesFromClipboard(stepIndex)}>
                                    Paste from Clipboard
                                  </Button>
                                  <Button size="small" onClick={() => addAttributeValueToStep(stepIndex)}>
                                    Add Value
                                  </Button>
                                </div>
                              </div>
                              <Typography.Text type="secondary" className="small">
                                Paste format: one row per line as <code>value[TAB]identifier</code>. Also supports <code>value|identifier</code> or <code>value,identifier</code>.
                              </Typography.Text>
                              <Table
                                size="small"
                                rowKey={(_, idx) => `attr-${stepIndex}-${idx}`}
                                pagination={false}
                                dataSource={step.attribute_values || []}
                                columns={[
                                  {
                                    title: 'Selectable Value',
                                    key: 'value_label',
                                    render: (_, row, idx) => (
                                      <Input
                                        value={row.value_label}
                                        placeholder="e.g. Red, 220V, Large"
                                        onChange={(e) =>
                                          setEditor((prev) => {
                                            const steps = [...(prev.steps || [])];
                                            const values = [...(steps[stepIndex]?.attribute_values || [])];
                                            values[idx] = { ...values[idx], value_label: e.target.value };
                                            steps[stepIndex] = { ...steps[stepIndex], attribute_values: values };
                                            return { ...prev, steps };
                                          })
                                        }
                                      />
                                    ),
                                  },
                                  {
                                    title: 'Part Identifier',
                                    key: 'identifier',
                                    width: 240,
                                    render: (_, row, idx) => (
                                      <Input
                                        value={row.identifier}
                                        placeholder="e.g. R, 220, LG"
                                        onChange={(e) =>
                                          setEditor((prev) => {
                                            const steps = [...(prev.steps || [])];
                                            const values = [...(steps[stepIndex]?.attribute_values || [])];
                                            values[idx] = { ...values[idx], identifier: e.target.value };
                                            steps[stepIndex] = { ...steps[stepIndex], attribute_values: values };
                                            return { ...prev, steps };
                                          })
                                        }
                                      />
                                    ),
                                  },
                                  {
                                    title: 'Action',
                                    key: 'action',
                                    width: 100,
                                    render: (_, __, idx) => (
                                      <Button
                                        danger
                                        size="small"
                                        onClick={() =>
                                          setEditor((prev) => {
                                            const steps = [...(prev.steps || [])];
                                            const values = [...(steps[stepIndex]?.attribute_values || [])].filter((_, vIdx) => vIdx !== idx);
                                            steps[stepIndex] = { ...steps[stepIndex], attribute_values: values };
                                            return { ...prev, steps };
                                          })
                                        }
                                      >
                                        Remove
                                      </Button>
                                    ),
                                  },
                                ]}
                                locale={{ emptyText: 'Add at least one selectable value.' }}
                              />
                            </div>
                          ) : (
                            <div>
                              <div className="small text-muted mb-1">
                                Selected for this step: <strong>{(step.products || []).length}</strong>. Set the Part Identifier for each item.
                              </div>
                              <Row gutter={[8, 8]}>
                                {(step.products || []).map((row, idx) => (
                                  <Col xs={24} md={12} xl={8} key={row.product_code}>
                                    <Card size="small" bodyStyle={{ padding: 10 }}>
                                      <div className="d-flex gap-2">
                                        <div
                                          className="border rounded bg-light d-flex align-items-center justify-content-center"
                                          style={{ width: 68, height: 68, flexShrink: 0, overflow: 'hidden' }}
                                        >
                                          <ImageWithFallback
                                            src={row.product?.image_url}
                                            alt={row.product?.name || row.product_code}
                                            imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                          />
                                        </div>
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                          <div className="small fw-semibold text-truncate">{row.product?.name || row.product_code}</div>
                                          <div className="small text-muted"><code>{row.product_code}</code></div>
                                          <Input
                                            size="small"
                                            className="mt-1"
                                            placeholder="Part Identifier"
                                            value={row.identifier}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setEditor((prev) => {
                                                const steps = [...(prev.steps || [])];
                                                const products = [...(steps[stepIndex]?.products || [])];
                                                products[idx] = { ...products[idx], identifier: value };
                                                steps[stepIndex] = { ...steps[stepIndex], products };
                                                return { ...prev, steps };
                                              });
                                            }}
                                          />
                                          <div className="d-flex justify-content-end mt-1">
                                            <Button
                                              size="small"
                                              danger
                                              onClick={() =>
                                                setEditor((prev) => {
                                                  const steps = [...(prev.steps || [])];
                                                  const products = [...(steps[stepIndex]?.products || [])].filter((_, pIdx) => pIdx !== idx);
                                                  steps[stepIndex] = { ...steps[stepIndex], products };
                                                  return { ...prev, steps };
                                                })
                                              }
                                            >
                                              Remove
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </Card>
                                  </Col>
                                ))}
                                {(step.products || []).length === 0 ? (
                                  <Col xs={24}>
                                    <Card size="small">
                                      <Typography.Text type="secondary">No products selected for this step yet.</Typography.Text>
                                    </Card>
                                  </Col>
                                ) : null}
                              </Row>
                            </div>
                          ),
                        },
                        {
                          key: 'search',
                          label: 'Search and Add Products to Step',
                          disabled: step.selection_mode === 'attribute',
                          children: step.selection_mode === 'attribute' ? (
                            <Typography.Text type="secondary">
                              This step is in attribute mode. Use the Selected Attributes tab to manage options.
                            </Typography.Text>
                          ) : (
                            <div>
                              <Row gutter={[8, 8]}>
                                <Col xs={24} md={12}>
                                  <Input
                                    placeholder="Search products"
                                    value={step.search_query}
                                    prefix={<Search size={14} />}
                                    onPressEnter={() => runStepSearch(stepIndex)}
                                    onChange={(e) =>
                                      setEditor((prev) => {
                                        const steps = [...(prev.steps || [])];
                                        steps[stepIndex] = { ...steps[stepIndex], search_query: e.target.value };
                                        return { ...prev, steps };
                                      })
                                    }
                                  />
                                </Col>
                                <Col xs={24} md={6}>
                                  <Select
                                    mode="multiple"
                                    allowClear
                                    className="w-100"
                                    value={step.filters?.category || []}
                                    placeholder="Facet: Category"
                                    options={(step.facets?.category || []).map((item) => ({
                                      value: item.value,
                                      label: `${item.value} (${item.count})`,
                                    }))}
                                    onChange={(value) =>
                                      setEditor((prev) => {
                                        const steps = [...(prev.steps || [])];
                                        const nextFilters = { ...(steps[stepIndex]?.filters || {}), category: value };
                                        steps[stepIndex] = { ...steps[stepIndex], filters: nextFilters };
                                        return { ...prev, steps };
                                      })
                                    }
                                  />
                                </Col>
                                <Col xs={24} md={4}>
                                  <Select
                                    mode="multiple"
                                    allowClear
                                    className="w-100"
                                    value={step.filters?.manufacturerName || []}
                                    placeholder="Facet: Manufacturer"
                                    options={(step.facets?.manufacturerName || []).map((item) => ({
                                      value: item.value,
                                      label: `${item.value} (${item.count})`,
                                    }))}
                                    onChange={(value) =>
                                      setEditor((prev) => {
                                        const steps = [...(prev.steps || [])];
                                        const nextFilters = { ...(steps[stepIndex]?.filters || {}), manufacturerName: value };
                                        steps[stepIndex] = { ...steps[stepIndex], filters: nextFilters };
                                        return { ...prev, steps };
                                      })
                                    }
                                  />
                                </Col>
                                <Col xs={24} md={2}>
                                  <Button className="w-100" onClick={() => runStepSearch(stepIndex)}>
                                    Search
                                  </Button>
                                </Col>
                              </Row>
                              <div className="small text-muted mb-1 mt-2">
                                Product search results for this step ({step.search_total || 0} found). Click "Add to Step" to include a product.
                              </div>
                              <Row gutter={[8, 8]}>
                                {(step.search_results || []).map((row) => {
                                  const code = String(row.productCode || row.product_code || '').trim();
                                  const alreadySelected = (step.products || []).some((item) => item.product_code === code);
                                  return (
                                    <Col xs={24} md={12} xl={8} key={row.id || code}>
                                      <Card
                                        size="small"
                                        className="h-100"
                                        bodyStyle={{ padding: 10 }}
                                        title={<span className="small fw-semibold">{row.name || code}</span>}
                                        extra={alreadySelected ? <Tag color="green">Selected</Tag> : null}
                                      >
                                        <div className="d-flex gap-2">
                                          <div
                                            className="border rounded bg-light d-flex align-items-center justify-content-center"
                                            style={{ width: 78, height: 78, flexShrink: 0, overflow: 'hidden' }}
                                          >
                                            <ImageWithFallback
                                              src={row.imageUrl}
                                              alt={row.name || code}
                                              imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                          </div>
                                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <div className="small"><code>{code}</code></div>
                                            <div className="small text-muted text-truncate">{row.manufacturerPartNumber || 'No MPN'}</div>
                                            <div className="small text-muted text-truncate">{row.category || 'No category'}</div>
                                            <div className="d-flex gap-1 mt-2">
                                              <Button
                                                size="small"
                                                icon={<Eye size={13} />}
                                                onClick={() => openProductDetails(row, code)}
                                              >
                                                Details
                                              </Button>
                                              <Button
                                                size="small"
                                                type={alreadySelected ? 'default' : 'primary'}
                                                disabled={alreadySelected}
                                                onClick={() => addProductsToStep(stepIndex, [row])}
                                              >
                                                {alreadySelected ? 'Added' : 'Add to Step'}
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </Card>
                                    </Col>
                                  );
                                })}
                                {!step.search_loading && (!step.search_results || step.search_results.length === 0) ? (
                                  <Col xs={24}>
                                    <Card size="small">
                                      <Typography.Text type="secondary">Run product search to list products for this step.</Typography.Text>
                                    </Card>
                                  </Col>
                                ) : null}
                              </Row>
                            </div>
                          ),
                        },
                      ]}
                    />
                  </Col>
                </Row>
                </Card>
                );
              })()}
            </>
            ) : null}
      </Card>
      <div
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 1200,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          borderRadius: 10,
        }}
      >
        <Space>
          <Button size="large" loading={saving} onClick={saveDefinition}>
            Save Draft
          </Button>
          <Button
            type="primary"
            size="large"
            loading={saving}
            disabled={!editor.id || !editor.is_ready}
            onClick={publishDefinition}
          >
            Publish
          </Button>
        </Space>
      </div>

      <Drawer
        open={ruleEditorDrawerOpen && activeRuleEditorIdx != null && Boolean(activeRuleEditor)}
        title={`Rule Editor${activeRuleEditorIdx != null ? ` #${activeRuleEditorIdx + 1}` : ''}`}
        placement="right"
        width={`${ruleDrawerWidthPct}vw`}
        destroyOnClose={false}
        onClose={() => setRuleEditorDrawerOpen(false)}
      >
        <div
          role="separator"
          aria-label="Resize rule drawer"
          onMouseDown={handleRuleDrawerResizeStart}
          style={{
            position: 'absolute',
            left: -4,
            top: 0,
            width: 8,
            height: '100%',
            cursor: 'ew-resize',
            zIndex: 30,
          }}
        />
        {activeRuleEditor ? (
          <div className="d-flex flex-column gap-3">
            <Alert
              type="info"
              showIcon
              message="Quick setup (source -> unavailable options)"
              description="Choose a source step and values, then choose which options become unavailable in a target step."
            />
            {(() => {
              const rule = activeRuleEditor;
              const firstGroup = Array.isArray(rule.groups) && rule.groups.length ? rule.groups[0] : blankRuleGroup();
              const firstCondition = Array.isArray(firstGroup.conditions) && firstGroup.conditions.length
                ? firstGroup.conditions[0]
                : blankRuleCondition();
              const sourceStep = getEditorStepByOrder(firstCondition.source_step_order);
              const sourceOptions = getStepOptionChoices(sourceStep);
              const targetStep = getEditorStepByOrder(rule.target_step_order);
              const targetOptions = getStepOptionChoices(targetStep);
              return (
                <Card size="small" title="Quick Rule Mapping">
                  <Row gutter={[8, 8]}>
                    <Col xs={24} md={12}>
                      <Form.Item label="When selected in step" className="mb-1">
                        <Select
                          className="w-100"
                          value={Number(firstCondition.source_step_order || 1)}
                          options={editorStepOrderOptions}
                          onChange={(value) => {
                            const sourceStepLike = getEditorStepByOrder(value);
                            const allowed = new Set(getStepOptionChoices(sourceStepLike).map((item) => item.value));
                            updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                              ...current,
                              groups: [
                                {
                                  ...(Array.isArray(current.groups) && current.groups.length ? current.groups[0] : blankRuleGroup()),
                                  operator: String(current.groups?.[0]?.operator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND',
                                  conditions: [
                                    {
                                      ...(Array.isArray(current.groups?.[0]?.conditions) && current.groups[0].conditions.length
                                        ? current.groups[0].conditions[0]
                                        : blankRuleCondition()),
                                      source_step_order: Number(value || 1),
                                      match_mode: current.groups?.[0]?.conditions?.[0]?.match_mode === 'all' ? 'all' : 'any',
                                      option_refs: (current.groups?.[0]?.conditions?.[0]?.option_refs || []).filter((ref) => allowed.has(ref)),
                                    },
                                  ],
                                },
                              ],
                              group_operator: String(current.group_operator || 'OR').toUpperCase() === 'AND' ? 'AND' : 'OR',
                            }));
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Values in source step" className="mb-1">
                        <Select
                          mode="multiple"
                          allowClear
                          className="w-100"
                          value={Array.isArray(firstCondition.option_refs) ? firstCondition.option_refs : []}
                          options={sourceOptions}
                          onChange={(value) => {
                            updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                              ...current,
                              groups: [
                                {
                                  ...(Array.isArray(current.groups) && current.groups.length ? current.groups[0] : blankRuleGroup()),
                                  operator: String(current.groups?.[0]?.operator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND',
                                  conditions: [
                                    {
                                      ...(Array.isArray(current.groups?.[0]?.conditions) && current.groups[0].conditions.length
                                        ? current.groups[0].conditions[0]
                                        : blankRuleCondition()),
                                      option_refs: value || [],
                                      match_mode: current.groups?.[0]?.conditions?.[0]?.match_mode === 'all' ? 'all' : 'any',
                                    },
                                  ],
                                },
                              ],
                              group_operator: String(current.group_operator || 'OR').toUpperCase() === 'AND' ? 'AND' : 'OR',
                            }));
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Rule group logic (between groups)" className="mb-1">
                        <Select
                          className="w-100"
                          value={String(rule.group_operator || 'OR').toUpperCase()}
                          options={[
                            { value: 'OR', label: 'Match any group (OR)' },
                            { value: 'AND', label: 'Match all groups (AND)' },
                          ]}
                          onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                            ...current,
                            group_operator: value === 'AND' ? 'AND' : 'OR',
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Current group condition logic" className="mb-1">
                        <Select
                          className="w-100"
                          value={String(firstGroup.operator || 'AND').toUpperCase()}
                          options={[
                            { value: 'AND', label: 'All conditions in group (AND)' },
                            { value: 'OR', label: 'Any condition in group (OR)' },
                          ]}
                          onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                            ...current,
                            groups: [
                              {
                                ...(Array.isArray(current.groups) && current.groups.length ? current.groups[0] : blankRuleGroup()),
                                operator: value === 'OR' ? 'OR' : 'AND',
                                conditions: Array.isArray(current.groups?.[0]?.conditions) && current.groups[0].conditions.length
                                  ? current.groups[0].conditions
                                  : [blankRuleCondition()],
                              },
                              ...((Array.isArray(current.groups) ? current.groups : []).slice(1)),
                            ],
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Then in step" className="mb-1">
                        <Select
                          className="w-100"
                          value={Number(rule.target_step_order || 1)}
                          options={editorStepOrderOptions}
                          onChange={(value) => {
                            const targetStepLike = getEditorStepByOrder(value);
                            const allowed = new Set(getStepOptionChoices(targetStepLike).map((item) => item.value));
                            updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                              ...current,
                              target_step_order: Number(value || 1),
                              target_option_refs: (current.target_option_refs || []).filter((ref) => allowed.has(ref)),
                            }));
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Unavailable behavior" className="mb-1">
                        <Select
                          className="w-100"
                          value={rule.effect === 'disable' ? 'disable' : 'hide'}
                          options={[
                            { value: 'disable', label: 'Disable options' },
                            { value: 'hide', label: 'Hide options' },
                          ]}
                          onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                            ...current,
                            effect: value === 'hide' ? 'hide' : 'disable',
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Options unavailable in target step" className="mb-1">
                        <Select
                          mode="multiple"
                          allowClear
                          className="w-100"
                          value={Array.isArray(rule.target_option_refs) ? rule.target_option_refs : []}
                          options={targetOptions}
                          onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                            ...current,
                            target_option_refs: value || [],
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Rule name" className="mb-1">
                        <Input
                          value={rule.name || ''}
                          placeholder={`Rule ${activeRuleEditorIdx + 1}`}
                          onChange={(e) => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                            ...current,
                            name: e.target.value,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              );
            })()}
            {(() => {
              const rule = activeRuleEditor;
              const groups = Array.isArray(rule.groups) ? rule.groups : [];
              return (
                <Card size="small" title="Advanced Group Builder">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <div>
                      <Typography.Text strong>Rule Group Logic</Typography.Text>
                      <Typography.Text type="secondary" className="small d-block">
                        Choose whether any group or all groups must match.
                      </Typography.Text>
                    </div>
                    <Space>
                      <Select
                        size="small"
                        style={{ width: 200 }}
                        value={String(rule.group_operator || 'OR').toUpperCase()}
                        options={[
                          { value: 'OR', label: 'Match any group (OR)' },
                          { value: 'AND', label: 'Match all groups (AND)' },
                        ]}
                        onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                          ...current,
                          group_operator: value === 'AND' ? 'AND' : 'OR',
                        }))}
                      />
                      <Button
                        size="small"
                        onClick={() => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                          ...current,
                          groups: [...(Array.isArray(current.groups) ? current.groups : []), blankRuleGroup()],
                        }))}
                      >
                        Add Group
                      </Button>
                    </Space>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    {groups.map((group, groupIdx) => (
                      <Card
                        key={group.id || `drawer-group-${groupIdx}`}
                        size="small"
                        title={`Group ${groupIdx + 1}`}
                        extra={(
                          <Space>
                            <Select
                              size="small"
                              style={{ width: 180 }}
                              value={String(group.operator || 'AND').toUpperCase()}
                              options={[
                                { value: 'AND', label: 'All conditions (AND)' },
                                { value: 'OR', label: 'Any condition (OR)' },
                              ]}
                              onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => {
                                const nextGroups = [...(Array.isArray(current.groups) ? current.groups : [])];
                                nextGroups[groupIdx] = { ...nextGroups[groupIdx], operator: value === 'OR' ? 'OR' : 'AND' };
                                return { ...current, groups: nextGroups };
                              })}
                            />
                            <Button
                              size="small"
                              onClick={() => updateRuleAtIndex(activeRuleEditorIdx, (current) => {
                                const nextGroups = [...(Array.isArray(current.groups) ? current.groups : [])];
                                nextGroups[groupIdx] = {
                                  ...nextGroups[groupIdx],
                                  conditions: [...(nextGroups[groupIdx]?.conditions || []), blankRuleCondition()],
                                };
                                return { ...current, groups: nextGroups };
                              })}
                            >
                              Add Condition
                            </Button>
                            <Button
                              size="small"
                              danger
                              onClick={() => updateRuleAtIndex(activeRuleEditorIdx, (current) => ({
                                ...current,
                                groups: [...(Array.isArray(current.groups) ? current.groups : [])].filter((_, idx) => idx !== groupIdx),
                              }))}
                            >
                              Remove Group
                            </Button>
                          </Space>
                        )}
                      >
                        {(group.conditions || []).map((condition, condIdx) => {
                          const sourceStep = getEditorStepByOrder(condition.source_step_order);
                          const sourceOptions = getStepOptionChoices(sourceStep);
                          return (
                            <Row key={condition.id || `drawer-condition-${condIdx}`} gutter={[8, 8]} className="mb-2">
                              <Col xs={24} md={7}>
                                <Select
                                  className="w-100"
                                  value={Number(condition.source_step_order || 1)}
                                  options={editorStepOrderOptions}
                                  onChange={(value) => {
                                    const sourceStepLike = getEditorStepByOrder(value);
                                    const allowed = new Set(getStepOptionChoices(sourceStepLike).map((item) => item.value));
                                    updateRuleAtIndex(activeRuleEditorIdx, (current) => {
                                      const nextGroups = [...(Array.isArray(current.groups) ? current.groups : [])];
                                      const nextConditions = [...(nextGroups[groupIdx]?.conditions || [])];
                                      nextConditions[condIdx] = {
                                        ...nextConditions[condIdx],
                                        source_step_order: Number(value || 1),
                                        option_refs: (nextConditions[condIdx]?.option_refs || []).filter((ref) => allowed.has(ref)),
                                      };
                                      nextGroups[groupIdx] = { ...nextGroups[groupIdx], conditions: nextConditions };
                                      return { ...current, groups: nextGroups };
                                    });
                                  }}
                                />
                              </Col>
                              <Col xs={24} md={5}>
                                <Select
                                  className="w-100"
                                  value={condition.match_mode === 'all' ? 'all' : 'any'}
                                  options={[
                                    { value: 'any', label: 'Any' },
                                    { value: 'all', label: 'All' },
                                  ]}
                                  onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => {
                                    const nextGroups = [...(Array.isArray(current.groups) ? current.groups : [])];
                                    const nextConditions = [...(nextGroups[groupIdx]?.conditions || [])];
                                    nextConditions[condIdx] = {
                                      ...nextConditions[condIdx],
                                      match_mode: value === 'all' ? 'all' : 'any',
                                    };
                                    nextGroups[groupIdx] = { ...nextGroups[groupIdx], conditions: nextConditions };
                                    return { ...current, groups: nextGroups };
                                  })}
                                />
                              </Col>
                              <Col xs={24} md={9}>
                                <Select
                                  mode="multiple"
                                  allowClear
                                  className="w-100"
                                  value={Array.isArray(condition.option_refs) ? condition.option_refs : []}
                                  options={sourceOptions}
                                  onChange={(value) => updateRuleAtIndex(activeRuleEditorIdx, (current) => {
                                    const nextGroups = [...(Array.isArray(current.groups) ? current.groups : [])];
                                    const nextConditions = [...(nextGroups[groupIdx]?.conditions || [])];
                                    nextConditions[condIdx] = { ...nextConditions[condIdx], option_refs: value || [] };
                                    nextGroups[groupIdx] = { ...nextGroups[groupIdx], conditions: nextConditions };
                                    return { ...current, groups: nextGroups };
                                  })}
                                />
                              </Col>
                              <Col xs={24} md={3}>
                                <Button
                                  className="w-100"
                                  size="small"
                                  danger
                                  onClick={() => updateRuleAtIndex(activeRuleEditorIdx, (current) => {
                                    const nextGroups = [...(Array.isArray(current.groups) ? current.groups : [])];
                                    const nextConditions = [...(nextGroups[groupIdx]?.conditions || [])].filter((_, idx) => idx !== condIdx);
                                    nextGroups[groupIdx] = { ...nextGroups[groupIdx], conditions: nextConditions };
                                    return { ...current, groups: nextGroups };
                                  })}
                                >
                                  Remove
                                </Button>
                              </Col>
                            </Row>
                          );
                        })}
                      </Card>
                    ))}
                  </div>
                </Card>
              );
            })()}
            <Typography.Text type="secondary" className="small">
              Tip: this drawer edits the first group quickly; use the full cards below to add/manage extra groups and conditions.
            </Typography.Text>
          </div>
        ) : null}
      </Drawer>

      <Modal
        open={rulesHelpOpen}
        title="Conditional Rules Guide"
        width={920}
        footer={null}
        onCancel={() => setRulesHelpOpen(false)}
      >
        <div className="d-flex flex-column gap-3">
          <Card size="small" style={{ background: '#f8fbff', borderColor: '#d3e7ff' }}>
            <Typography.Text strong className="d-block mb-1">How to build a rule</Typography.Text>
            <Typography.Text className="d-block">
              1) Configure <strong>Then</strong> first: choose the target step, behavior (`hide` or `disable`), and optional target options.
            </Typography.Text>
            <Typography.Text className="d-block">
              2) Configure <strong>When</strong>: add one or more groups, and conditions in each group.
            </Typography.Text>
            <Typography.Text className="d-block">
              3) Use the top group operator to combine groups, and each group operator to combine conditions.
            </Typography.Text>
          </Card>

          <Card size="small" title="How the engine interprets rules">
            <Typography.Text className="d-block">
              - Each rule is evaluated against current selections in the builder/cart-edit.
            </Typography.Text>
            <Typography.Text className="d-block">
              - If the `When` section is true, the `Then` action is applied to the target step.
            </Typography.Text>
            <Typography.Text className="d-block">
              - `hide`: option is removed from the list. `disable`: option remains visible and cannot be selected.
            </Typography.Text>
            <Typography.Text className="d-block">
              - If a selected option becomes restricted by a matching rule, the app clears it and shows a message.
            </Typography.Text>
          </Card>

          <Card size="small" title="Example">
            <Typography.Text className="d-block">
              <strong>Goal:</strong> if users pick `208V` in Step 1 (Voltage), restrict some accessories in Step 3.
            </Typography.Text>
            <Typography.Text className="d-block">
              <strong>Then:</strong> Target Step = Step 3, Behavior = `disable`, Target Options = `Accessory-A`, `Accessory-B`.
            </Typography.Text>
            <Typography.Text className="d-block">
              <strong>When:</strong> Group 1 has condition Source Step = Step 1, Match Mode = `any`, Match Options = `208V`.
            </Typography.Text>
            <Typography.Text className="d-block">
              Result: whenever Step 1 is `208V`, those two accessories are shown but disabled.
            </Typography.Text>
          </Card>
        </div>
      </Modal>

      <Modal
        open={appAlert.open}
        title={appAlert.title || 'Notice'}
        onOk={() => setAppAlert({ open: false, title: 'Notice', message: '' })}
        onCancel={() => setAppAlert({ open: false, title: 'Notice', message: '' })}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Typography.Text>{appAlert.message}</Typography.Text>
      </Modal>

      <Modal
        open={appConfirm.open}
        title={appConfirm.title || 'Confirm'}
        okText="Yes"
        cancelText="No"
        onOk={async () => {
          const confirmHandler = appConfirm.onConfirm;
          setAppConfirm({ open: false, title: '', message: '', onConfirm: null });
          if (typeof confirmHandler === 'function') {
            await confirmHandler();
          }
        }}
        onCancel={() => setAppConfirm({ open: false, title: '', message: '', onConfirm: null })}
      >
        <Typography.Text>{appConfirm.message}</Typography.Text>
      </Modal>

      <Modal
        open={editorZoomOpen}
        title="Image Preview"
        footer={null}
        width={960}
        onCancel={() => {
          setEditorZoomOpen(false);
          setEditorZoomScale(1);
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
          <Typography.Text type="secondary">Full-size view</Typography.Text>
          <div className="d-flex align-items-center gap-2">
            <Button size="small" onClick={() => setEditorZoomScale((prev) => Math.max(1, Number((prev - 0.25).toFixed(2))))}>-</Button>
            <span className="small">{Math.round(editorZoomScale * 100)}%</span>
            <Button size="small" onClick={() => setEditorZoomScale((prev) => Math.min(4, Number((prev + 0.25).toFixed(2))))}>+</Button>
          </div>
        </div>
        {editorGalleryImages.length > 1 ? (
          <div className="d-flex gap-2 flex-nowrap overflow-auto pb-2 mb-2 border-bottom">
            {editorGalleryImages.map((img) => (
              <button
                key={`zoom-${img}`}
                type="button"
                className={`border rounded bg-white p-1 ${editorPreviewImage === img ? 'border-primary' : ''}`}
                style={{ width: 64, height: 64, flex: '0 0 auto' }}
                onClick={() => {
                  setEditorPreviewImage(img);
                  setEditorZoomImage(img);
                }}
              >
                <ImageWithFallback
                  src={img}
                  alt="zoom-thumb"
                  imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </button>
            ))}
          </div>
        ) : null}
        <div className="border rounded bg-light d-flex align-items-center justify-content-center" style={{ minHeight: 560, overflow: 'auto' }}>
          <ImageWithFallback
            src={editorZoomImage || editorPreviewImage}
            alt={editor.name || editor.product_code || 'editor-zoom'}
            imageStyle={{
              transform: `scale(${editorZoomScale})`,
              transformOrigin: 'center center',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
            fallbackClassName="text-muted"
          />
        </div>
      </Modal>

      <Modal
        open={builderZoomOpen}
        title="Image Zoom"
        footer={null}
        width={920}
        onCancel={() => {
          setBuilderZoomOpen(false);
          setBuilderZoomScale(1);
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-2">
          <Typography.Text type="secondary">Zoom</Typography.Text>
          <div className="d-flex align-items-center gap-2">
            <Button size="small" onClick={() => setBuilderZoomScale((prev) => Math.max(1, Number((prev - 0.25).toFixed(2))))}>-</Button>
            <span className="small">{Math.round(builderZoomScale * 100)}%</span>
            <Button size="small" onClick={() => setBuilderZoomScale((prev) => Math.min(4, Number((prev + 0.25).toFixed(2))))}>+</Button>
          </div>
        </div>
        {builderGalleryImages.length > 1 ? (
          <div className="d-flex gap-2 flex-nowrap overflow-auto pb-2 mb-2 border-bottom">
            {builderGalleryImages.map((img) => (
              <button
                key={`builder-zoom-${img}`}
                type="button"
                className={`border rounded bg-white p-1 ${(builderZoomImage || builderActiveImage) === img ? 'border-primary' : ''}`}
                style={{ width: BUILDER_THUMB_CANVAS_SIZE, height: BUILDER_THUMB_CANVAS_SIZE, flex: '0 0 auto' }}
                onClick={() => setBuilderZoomImage(img)}
              >
                <ImageWithFallback
                  src={img}
                  alt="builder-zoom-thumb"
                  imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </button>
            ))}
          </div>
        ) : null}
        <div className="border rounded bg-light d-flex align-items-center justify-content-center" style={{ minHeight: 520, overflow: 'auto' }}>
          <ImageWithFallback
            src={builderZoomImage || builderActiveImage}
            alt="zoom"
            imageStyle={{
              transform: `scale(${builderZoomScale})`,
              transformOrigin: 'center center',
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
            fallbackClassName="text-muted"
          />
        </div>
      </Modal>

      <Modal
        open={detailModal.open}
        title="Product Details"
        footer={null}
        onCancel={() => setDetailModal({ open: false, product: null })}
      >
        <div className="d-flex gap-3">
          <div
            className="border rounded bg-light d-flex align-items-center justify-content-center"
            style={{ width: 120, height: 120, flexShrink: 0, overflow: 'hidden' }}
          >
            <ImageWithFallback
              src={detailModal.product?.imageUrl}
              alt={detailModal.product?.name || detailModal.product?.productCode}
              imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
          <div className="small">
            <div><strong>Code:</strong> {detailModal.product?.productCode || '—'}</div>
            <div><strong>Name:</strong> {detailModal.product?.name || '—'}</div>
            <div><strong>Category:</strong> {detailModal.product?.category || '—'}</div>
            <div><strong>Manufacturer:</strong> {detailModal.product?.manufacturerName || '—'}</div>
            <div><strong>MPN:</strong> {detailModal.product?.manufacturerPartNumber || '—'}</div>
            <div className="mt-2 text-muted">
              {detailModal.product?.description || 'No description available.'}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ConfigurableProducts;
